import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format, utcToZonedTime } from 'date-fns-tz';
import ReactModal from 'react-modal';
import { TbSearch } from 'react-icons/tb';
import { RiSearchLine } from 'react-icons/ri';
import { FiEdit2, FiPlus } from 'react-icons/fi';
import InputMask from 'react-input-mask';
import { findManagers, findPlayer, getLinkedIndicators } from 'services/profile.service';
import { LinkedIndicators_Indicadores, User } from 'types/user';
import { useAuth } from 'context/AuthContext';
import {
  CheckTotalTasksParticipationMember,
  deleteMissionFeedback,
  deletePartialTask,
  getPlayerInfo,
  getServerTime,
  getTaskById,
  sendNotificationTask,
  sendPreferenceNotification,
  submitTask,
  updateTask,
  verifyTask,
  getPartialTask,
  sendPartialTask,
  findManagersMad,
} from 'services/dashboard.service';
import { MdOutlineExpandMore, MdOutlineExpandLess } from 'react-icons/md';

import { toast } from 'react-toastify';
import Modal from '../Modal/Index';
import TextEditor from 'components/text-editor';
import { useModal } from 'context/ModalContext';
import { useHelp } from 'context/HelpContext';
import { buildDemandanteOptions, DemandanteOption, isDemandanteFilled, isDemandanteOptionSelected } from './demandante-options';
import { dispensesDescription, requiresOwnDescription, validateParticipationMessages } from './participation-messages';
import { avaliarInclusao, ContextoDaInclusao } from './elegibilidade-do-participante';
import { consultarEmLotes } from './colagem-de-chaves';
import ColagemDeChaves, { RecusaDaColagem, ResumoDaColagem } from './ColagemDeChaves';
import ListaDeParticipantes from './ListaDeParticipantes';
import PainelDeSugestoes, { EstadoDasSugestoes } from './PainelDeSugestoes';
import { montarSecoes, percentualDaUltimaVez } from './sugestoes-de-participantes';
import { NoticeModal } from 'components/ui/notice-modal';
import { getParticipantsApprovalBand } from 'utils/participantsApproval';
import { PERIOD_END_MIN, PERIOD_START_MIN, isPeriodEndValid, isPeriodStartValid } from 'utils/delivery-period';

import './style.scss';
import {
  buscarSugestoesDeParticipantes,
  getNecs,
  getUors,
  getUorDemandantes,
  PessoaSugerida,
} from 'services/realizacao.service';
import { pushPlayerAction, actionLog } from 'services/actionlog.service';
import { expandManagerToDuplaOptions } from 'pages/MinhaEquipe/ApreciarRealizacao/mad/succession';
import { DuplaOption } from 'pages/MinhaEquipe/ApreciarRealizacao/shared/types';
import { nomeDoParticipante } from 'pages/RealizacaoDestaque/identidade-do-participante';
import Button from 'components/ui/button';
import { SelectField } from 'components/form';
import { FOCO_VISIVEL } from 'utils/foco';
import ModalDeCadastro, { DesfechoDoCadastro, EtapaDoCadastro } from './ModalDeCadastro';
import { AvisoDeValidacao, CAMPO, Iniciais, Nota, ROTULO, RostoDaPessoa } from './componentes-do-cadastro';
import {
  ORDEM_DE_EXIBICAO,
  etapaAnterior as etapaAnteriorNaTela,
  etapaAoRetomar,
  posicaoNaTela,
  primeiraEtapa,
  proximaEtapa,
  ultimaEtapa,
} from './ordem-das-etapas';

const maskTemplate = 'F9999999';

// Indexados pela etapa lógica (`step` 0 = participantes, 1 = informações, 2 = detalhes), que é o que o rascunho grava.
// A coluna segue a ordem do design (`ORDEM_DE_EXIBICAO`): Informações, Detalhes e resultados, Participantes (RCP-01).
const ETAPAS_DO_CADASTRO: EtapaDoCadastro[] = [
  { titulo: 'Participantes da entrega', descricao: 'Quem atuou com você' },
  { titulo: 'Informações da realização', descricao: 'Nome, indicador, NEC e prazos' },
  { titulo: 'Detalhes e resultados', descricao: 'O que mudou com sua entrega' },
];

const ETAPAS_NA_TELA = ORDEM_DE_EXIBICAO.map(etapa => ETAPAS_DO_CADASTRO[etapa]);

const TITULOS_DAS_ETAPAS = ['Quem participou desta entrega?', 'Informações da Realização Destaque', 'Detalhes e resultados'];

const BOTAO_DO_RESPONSAVEL = `flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-jj-field border border-brand-blue bg-brand-white text-brand-blue hover:bg-brand-blue hover:text-brand-yellow ${FOCO_VISIVEL}`;

// Deriva o parceiro pré-selecionado da dupla do gestor inicial sugerido (MOD-07):
// no fluxo MAD o gestor DIRETO já vem com a dupla dele pré-selecionada quando tem
// mad_dupla; sem dupla (ou fora do MAD) → undefined (opção solo). Fora do MAD é
// sempre undefined, mantendo o cadastro não-MAD intocado. Busca a primeira opção
// COM parceiro porque a expansão traz a opção individual à frente (a pré-seleção
// MOD-07 continua sendo a dupla). Helper puro de módulo (referência estável —
// não entra como dependência de hooks).
function deriveInitialPartner(isMad: boolean, gestor: any) {
  if (!isMad || !gestor) return undefined;

  const firstPair = expandManagerToDuplaOptions(gestor).find(option => !!option.parceiro?._id);

  return firstPair?.parceiro ?? undefined;
}

// Flag para controlar se os dados foram carregados de uma task parcial
let isLoadingPartialTask = false;

export default function MissaoDestaqueCreate() {
  const navigate = useNavigate();
  const params = useParams();

  const { user, userInfo, refreshUser, currentCycle, cycles } = useAuth();
  const { helpData } = useHelp();

  const [intervalId, setIntervalId] = useState<any>(null);

  const searchRef = useRef<any>();
  const messageRef = useRef<any>();
  const titleRef = useRef<any>();
  const periodStartRef = useRef<any>();
  const periodEndRef = useRef<any>();
  const savePartialTaskTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isOpen, setIsOpen] = useState(true);
  const [editing, setEditing] = useState(true);

  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [target, setTarget] = useState<User[]>();
  const [bkpTarget, setbkpTarget] = useState<User[]>();
  const [openTargetAlert, setOpenTargetAlert] = useState(false);
  const { showModal } = useModal();
  const [showModalTutorial, setshowModalTutorial] = useState(false);

  const [necsList, setNecsList] = useState<any>([]);
  const [demandanteList, setDemandanteList] = useState<DemandanteOption[]>([]);
  const [needNec, setNeedNec] = useState<any>([]);
  const [necCheckFailed, setNecCheckFailed] = useState(false);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isEmptyName, setIsEmptyName] = useState(false);
  const [isAlreadySelectedInList, setIsAlreadySelectedInList] = useState(false);
  const [showDemandante, setShowDemandante] = useState(false);
  const [necIsSearched, setNecIsSearched] = useState(false);
  const [demandanteValue, setDemandanteValue] = useState<any>([]);
  const [selectedNec, setSelectedNec] = useState<any>([]);

  const [teamDistribution, setTeamDistribution] = useState<any>({});

  const [periodStart, setPeriodStart] = useState<any>();
  const [periodEnd, setPeriodEnd] = useState<any>();
  const [nec, setNec] = useState<any>();

  const [title, setTitle] = useState<string>();
  const [titleLength, setTitleLength] = useState<{ textLength: number; fullLength: number } | undefined>();

  const [message, setMessage] = useState<string>();
  const [messageLength, setMessageLength] = useState<{ textLength: number; fullLength: number } | undefined>();

  const [resultMessage, setResultMessage] = useState<string>();
  const [resultLength, setResultLength] = useState<{ textLength: number; fullLength: number } | undefined>();

  const [indicator, setIndicator] = useState<
    { label: string; value: number | string; bloco?: string; cod_bloco?: string | number } | undefined
  >();
  const [indicatorsList, setIndicatorsList] = useState<LinkedIndicators_Indicadores[] | undefined>();

  const [searchingCurrentUser, setSearchingCurrentUser] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(false);
  // Os editores de texto leem `initialValue` só ao montar. Com Informações como primeira etapa, o formulário espera a
  // edição ou o rascunho chegarem; senão o nome gravado nunca aparece no campo.
  const [cargaInicialConcluida, setCargaInicialConcluida] = useState(false);
  const [sugestoes, setSugestoes] = useState<EstadoDasSugestoes>({ tipo: 'carregando' });
  const sugestoesPedidas = useRef(false);
  // Quem entrou com o percentual da última vez e ainda não teve o percentual trocado (SUG-20).
  const [comPercentualDaUltimaVez, setComPercentualDaUltimaVez] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);
  const [hasPartialTask, setHasPartialTask] = useState(false);

  const [step, setStep] = useState(primeiraEtapa());

  const [submitError, setSubmitError] = useState<string>();

  const [mask, setMask] = useState('');

  const [showModalAlert, setShowModalAlert] = useState<any>(false);

  const [currentTask, setCurrentTask] = useState<any>();
  const [maxMembersWithoutApproval, setMaxMembersWithoutApproval] = useState<number>(20);
  const [maxMembers, setMaxMembers] = useState<number | undefined>();

  const [initialManager, setInitialManager] = useState<any>();
  // Parceiro da dupla do gestor inicial (fluxo MAD, MOD-07). No cadastro NÃO-MAD
  // permanece sempre undefined — estado inerte que não altera o fluxo existente.
  const [initialPartner, setInitialPartner] = useState<any>();
  const [searchManager, setSearchManager] = useState<string>('');
  const [searchResultsManager, setSearchResultsManager] = useState<User[]>([]);
  // Resultados da busca manual como opções de dupla (fluxo MAD, MOD-07). Cada gestor
  // encontrado em gestores_mad é expandido em opção solo + uma opção por parceiro.
  const [searchResultsDupla, setSearchResultsDupla] = useState<DuplaOption[]>([]);

  const [minDate, setMinDate] = useState<string>();
  const [maxDate, setMaxDate] = useState<string>();
  const [ariaLabel, setAriaLabel] = useState('');

  const [hasOtherType, setHasOtherType] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const lastFocusedElementRef = useRef<HTMLElement | null>(null);

  const [usersNotification, setUsersNotification] = useState<string[]>([]);
  const [usersNotificationPreference, setUsersNotificationPreference] = useState<string[]>([]);

  ReactModal.setAppElement('#modal');

  const minTitleLength = 0;
  const maxTitleLength = 150;

  const minMessageLength = 0;
  const maxMessageLength = 600;

  const minResultLength = 0;
  const maxResultLength = 600;

  const minMessageTarget = 60;
  const maxMessageTarget = 200;

  ReactModal.setAppElement('#modal');

  const maxMembersByProfile = useMemo(() => {
    let maxM = 0;

    if (
      !!userInfo?.ro?.match(/(3GUE|2GUT|2GCA|2GUA)/gi) &&
      (typeof maxMembers !== 'number' || (typeof maxMembers === 'number' && maxMembers <= 30))
    ) {
      maxM = 30;
    } else if (
      !!userInfo?.ro?.match(/(2GUE|1GUT|1GUA|1GCA)/gi) &&
      (typeof maxMembers !== 'number' || (typeof maxMembers === 'number' && maxMembers <= 50))
    ) {
      maxM = 50;
    } else if (
      !![3003, 4005, 4007].includes(userInfo?.cod_funcao || 0) &&
      (typeof maxMembers !== 'number' || (typeof maxMembers === 'number' && maxMembers <= 100))
    ) {
      maxM = 100;
    } else if (!![3002].includes(userInfo?.cod_funcao || 0)) {
      maxM = 1000000;
    } else {
      if (typeof maxMembers === 'number') {
        maxM = maxMembers;
      } else {
        maxM = maxMembersWithoutApproval;
      }
    }

    return maxM;
  }, [maxMembers, maxMembersWithoutApproval, userInfo]);

  // `participantes` existe para quem acabou de chamar `setTarget`: o `target` desta renderização ainda é o anterior.
  const teamApproval = (participantes: User[] | undefined = target) => {
    const obj = {
      showAlert: false,
      showPending: false,
      isApproved: false,
      blockAdd: false,
      expired: false,
      rejected: false,
      taskStep: cycles?.find(x => {
        if (!!currentTask?._id) {
          return currentTask.created >= x.date_start && currentTask.created <= x.date_finish;
        } else {
          const date = +new Date();
          return date >= x.date_start && date <= x.date_finish;
        }
      }),
    };

    if (!!cycles && !!userInfo) {
      if (params?.id && params.id === 'cadastrar' && Array.isArray(participantes) && participantes.length > maxMembersWithoutApproval) {
        obj.showAlert = true;
      } else if (params?.id && params.id !== 'cadastrar') {
        if (!!currentTask?._id) {
          const timestamp = +new Date();
          const evaluationActive =
            !!obj.taskStep &&
            (obj.taskStep?.status === 'evaluation' ||
              (timestamp >= obj.taskStep.date_evaluation_step2_start && timestamp <= obj.taskStep.date_evaluation_step2_finish) ||
              (timestamp >= obj.taskStep.date_evaluation_step4_start && timestamp <= obj.taskStep.date_evaluation_step4_finish) ||
              (timestamp >= obj.taskStep.date_evaluation_step5_start && timestamp <= obj.taskStep.date_evaluation_step5_finish));

          if (obj.taskStep?.status === 'open') {
            if (Array.isArray(participantes) && participantes.length > maxMembersWithoutApproval) {
              obj.showAlert = true;
            }

            if (currentTask.participants_approval?.status === 'pending') {
              obj.showPending = true;
            }

            if (currentTask.participants_approval?.status === 'approved') {
              obj.isApproved = true;
            }

            if (currentTask.participants_approval?.status === 'rejected') {
              obj.rejected = true;
            }
          } else if (evaluationActive) {
            if (currentTask.participants_approval?.status === 'pending') {
              // obj.blockAdd = !!(Array.isArray(target) && target.length >= maxMembersWithoutApproval);
              obj.expired = true;
            } else if (currentTask.participants_approval?.status === 'approved') {
              obj.isApproved = true;
              // obj.blockAdd = !!(!maxMembers || (maxMembers && target?.length && target.length >= maxMembers));
            } else {
              // if (typeof maxMembers === 'number') {
              //   obj.blockAdd = !!(Array.isArray(target) && target.length >= maxMembers);
              // } else {
              //   obj.blockAdd = !!(Array.isArray(target) && target.length >= maxMembersWithoutApproval);
              // }
            }

            // A inclusão de participantes passou a ser livre: não há mais bloqueio por quantidade.
            // A faixa apenas define se a realização ficará aprovada ou pendente (vide submit).

            if (currentTask.participants_approval?.status === 'rejected') {
              obj.rejected = true;
            }
          } else {
            obj.blockAdd = true;
          }
        } else {
          obj.blockAdd = true;
        }
      }
    } else {
      obj.blockAdd = true;
    }

    return obj;
  };

  function formatTeamMemberErrorMessage(teamMemberError: any[]) {
    if (!Array.isArray(teamMemberError) || !teamMemberError.length) {
      return '';
    }

    const ids = teamMemberError.map(item => item._id),
      baseMessage = 'não podem ser adicionadas porque atingiram o limite de participações nesta fase.';

    if (ids.length === 1) {
      return `A matrícula ${ids[0]} não pode ser adicionada porque atingiu o limite de participações nesta fase.`;
    }

    const lastId = ids.pop();
    const joinedIds = ids.join(', ');

    return `As matrículas ${joinedIds} e ${lastId} ${baseMessage}`;
  }

  function handleChange() {
    if (searchRef.current) {
      const value = searchRef.current.value.toUpperCase();
      if (value.length > 1) {
        if (value[0].toUpperCase() === 'F' && !isNaN(value[1])) {
          setMask(maskTemplate);
        } else {
          setMask('');
          if (value === '_______') {
            searchRef.current.value = '';
          }
        }
      } else {
        setMask('');
      }
    }
  }

  /** Fase usada na busca de participantes: a da realização em edição ou a fase aberta. */
  function idDaFaseDaBusca() {
    return currentTask && currentTask.created
      ? cycles?.find(x => currentTask.created >= x.date_start && currentTask.created <= x.date_finish)?._id
      : currentCycle?._id;
  }

  async function handleSearch(e?: any) {
    if (e) {
      e.preventDefault();
    }

    sendPartialLog({ type: 'search', data: searchRef?.current?.value });

    try {
      setOpenTargetAlert(false);
      const matricula = searchRef.current.value.replace('_', '');

      const targetOnList = target?.find(item => item._id === matricula);

      if (targetOnList) {
        setIsAlreadySelectedInList(true);
        setSearching(false);
        return;
      }

      setSearching(true);

      const result = await findPlayer(searchRef.current.value, true, true, true, false, idDaFaseDaBusca());

      const filteredResults = result.filter((item: any) => item._id !== user?._id);
      const isCurrentUser = result.filter((item: any) => item._id === user?._id);

      if (filteredResults) {
        setSearchResults(filteredResults);

        setIsEmpty(filteredResults?.length === 0 && matricula.startsWith('F') && matricula.length === 8);
        setIsEmptyName(filteredResults?.length === 0 && isCurrentUser.length === 0);
      }

      setSearching(false);
    } catch (e) {
      setSearching(false);
    }
  }

  async function handleSearchManager(e?: any) {
    if (e) {
      e.preventDefault();
    }

    sendPartialLog({ type: 'searchManager', data: searchManager });

    try {
      setSearching(true);

      if (userInfo?.mad) {
        // Fluxo MAD (MOD-07): busca em gestores_mad e expande cada gestor em opções
        // de dupla (solo + uma por parceiro). Mantém o range de níveis existente.
        const result = await findManagersMad(searchManager, [1, 2, 3, 4, 5]);

        const expanded = (Array.isArray(result) ? result : [])
          .filter((item: any) => item._id !== user?._id)
          .flatMap((manager: any) => expandManagerToDuplaOptions(manager));

        setSearchResultsDupla(expanded);
      } else {
        // hideDupla: no cadastramento (fluxo normal) gestores cod_etapa 1/2/3 com dupla
        // de liderança não são opções — essas apreciações acontecem pelo fluxo MAD.
        const result = await findManagers(searchManager, [1, 2, 3, 4, 5], true, false, true);

        const filteredResults = result.filter((item: any) => item._id !== user?._id);

        setSearchResultsManager(filteredResults);
      }

      setSearching(false);
    } catch (e) {
      setSearching(false);
    }
  }

  function sendPartialLog(action: any) {
    try {
      const obj = {
        action: !!action ? action : 'ND',
        title: !!title ? title : 'ND',
        loading: !!loading,
        step: !!step ? step : 'ND',
        team:
          Array.isArray(target) && target.length
            ? target.map(item => {
                return {
                  _id: item._id,
                  cad_missao: item.extra?.cad_missao,
                  cod_etapa: item.extra?.cod_etapa,
                  funcao_localizacao: item.extra?.funcao_localizacao,
                  message: item.message,
                };
              })
            : 'ND',
        currentTask: !!currentTask ? currentTask : 'ND',
        stage: {
          status: currentCycle && currentCycle.status ? currentCycle?.status : 'ND',
          date_start:
            currentCycle && currentCycle.date_start
              ? typeof currentCycle.date_start === 'number'
                ? { $date: currentCycle.date_start }
                : currentCycle.date_start
              : 'ND',
          date_finish:
            currentCycle && currentCycle.date_finish
              ? typeof currentCycle.date_finish === 'number'
                ? { $date: currentCycle.date_finish }
                : currentCycle.date_finish
              : 'ND',
        },
        start: !!periodStart ? { $date: new Date(periodStart).getTime() } : 'ND',
        end: !!periodEnd ? { $date: new Date(periodEnd).getTime() } : 'ND',
        nec: !!selectedNec ? selectedNec.value : 'ND',
        indicator: !!indicator
          ? {
              nome: indicator?.label || undefined,
              cod: indicator?.value || undefined,
              bloco: (indicator as any)?.bloco || undefined,
              cod_bloco: (indicator as any)?.cod_bloco || undefined,
            }
          : 'ND',
        description: !!message ? message : 'ND',
        result: !!resultMessage ? resultMessage : 'ND',
        original_manager: !!initialManager ? initialManager._id : 'ND',
        // Fluxo MAD (MOD-07): telemetria do parceiro da dupla. Fora do MAD não é adicionado.
        ...(userInfo?.mad ? { non_evaluator: initialPartner?._id ? [initialPartner._id] : [] } : {}),
      };

      if (obj.currentTask?._id) {
        delete obj.currentTask.target;
        delete obj.currentTask.evaluator_detail;
        delete obj.currentTask.non_evaluator_detail;
        delete obj.currentTask.owner_name;
        delete obj.currentTask.owner_nome_dependencia;
        delete obj.currentTask.owner_funcao;
        delete obj.currentTask.owner_tipo_classificacao;
        delete obj.currentTask.owner_avatar;
        delete obj.currentTask.original_manager_detail;
        delete obj.currentTask.gestores;
        delete obj.currentTask.feedback;
        delete obj.currentTask.now;
        delete obj.currentTask.answeredQuiz;
        delete obj.currentTask.mad_manager_appreciated;
      }

      pushPlayerAction('partialTask', {
        data: obj,
      });
    } catch (error) {}
  }

  // Tipo para os campos que podem ser atualizados
  type PartialTaskUpdate = {
    target?: User[];
    teamDistribution?: any;
    title?: string;
    indicator?: { label: string; value: number | string };
    periodStart?: string;
    periodEnd?: string;
    selectedNec?: any;
    demandanteValue?: any;
    initialManager?: any;
    initialPartner?: any;
    message?: string;
    resultMessage?: string;
    step?: number;
  };

  async function savePartialTask(updates?: PartialTaskUpdate) {
    if (isLoadingPartialTask || (params?.id && params.id !== 'cadastrar')) {
      return;
    }

    try {
      // Usar valores passados como parâmetro (frescos) ou do estado
      const currentTarget = updates?.target !== undefined ? updates.target : target;
      const currentTeamDistribution = updates?.teamDistribution !== undefined ? updates.teamDistribution : teamDistribution;
      const currentTitle = updates?.title !== undefined ? updates.title : title;
      const currentIndicator = updates?.indicator !== undefined ? updates.indicator : indicator;
      const currentPeriodStart = updates?.periodStart !== undefined ? updates.periodStart : periodStart;
      const currentPeriodEnd = updates?.periodEnd !== undefined ? updates.periodEnd : periodEnd;
      const currentSelectedNec = updates?.selectedNec !== undefined ? updates.selectedNec : selectedNec;
      const currentDemandanteValue = updates?.demandanteValue !== undefined ? updates.demandanteValue : demandanteValue;
      const currentInitialManager = updates?.initialManager !== undefined ? updates.initialManager : initialManager;
      const currentInitialPartner = updates?.initialPartner !== undefined ? updates.initialPartner : initialPartner;
      const currentMessage = updates?.message !== undefined ? updates.message : message;
      const currentResultMessage = updates?.resultMessage !== undefined ? updates.resultMessage : resultMessage;
      const currentStep = updates?.step !== undefined ? updates.step : step;

      if (currentStep === 0 && (!Array.isArray(currentTarget) || currentTarget.length < 5)) {
        return;
      }

      const partialTeam =
        Array.isArray(currentTarget) && currentTarget.length
          ? currentTarget.map(item => {
              return {
                _id: item._id,
                cad_missao: item.extra?.cad_missao,
                cod_etapa: item.extra?.cod_etapa,
                funcao_localizacao: item.extra?.funcao_localizacao,
                message: item.message,
                percent: currentTeamDistribution?.[item._id] || undefined,
              };
            })
          : undefined;

      const partialTask: any = {
        step: currentStep,
      };

      // Step 0 data
      if (partialTeam && partialTeam.length > 0) {
        partialTask.team = partialTeam;
      }

      // Step 1 data
      if (currentTitle) {
        partialTask.title = currentTitle;
      }

      if (currentIndicator?.label && currentIndicator?.value) {
        partialTask.indicator = {
          nome: currentIndicator.label,
          cod: currentIndicator.value,
          bloco: (currentIndicator as any)?.bloco || undefined,
          cod_bloco: (currentIndicator as any)?.cod_bloco || undefined,
        };
      }

      if (currentPeriodStart) {
        partialTask.start = { $date: new Date(currentPeriodStart).getTime() };
      }

      if (currentPeriodEnd) {
        partialTask.end = { $date: new Date(currentPeriodEnd).getTime() };
      }

      if (currentSelectedNec?.value) {
        partialTask.nec = currentSelectedNec.value;
      }

      if (currentDemandanteValue) {
        partialTask.demandante =
          typeof currentDemandanteValue === 'object' ? currentDemandanteValue.value : currentDemandanteValue;
      }

      if (currentInitialManager?._id) {
        partialTask.original_manager = currentInitialManager._id;

        // Fluxo MAD (MOD-07): persiste o parceiro da dupla como non_evaluator (array
        // vazio na opção solo) para round-trip do rascunho. Fora do MAD, não é tocado.
        if (userInfo?.mad) {
          partialTask.non_evaluator = currentInitialPartner?._id ? [currentInitialPartner._id] : [];
        }
      }

      // Step 2 data
      if (currentMessage) {
        partialTask.description = currentMessage;
      }

      if (currentResultMessage) {
        partialTask.result = currentResultMessage;
      }

      const hasDataToSave = Object.keys(partialTask).length > 1;
      await sendPartialTask(hasDataToSave ? partialTask : null);
      if (hasDataToSave) {
        setHasPartialTask(true);
      }
    } catch (error) {
      console.error('Erro ao salvar task parcial:', error);
    }
  }

  // Função com debounce para campos de texto (evita envios excessivos durante digitação)
  function debouncedSavePartialTask(updates?: PartialTaskUpdate, delay = 1500) {
    // Cancela o timer anterior se existir
    if (savePartialTaskTimerRef.current) {
      clearTimeout(savePartialTaskTimerRef.current);
    }

    // Agenda novo envio após o delay
    savePartialTaskTimerRef.current = setTimeout(() => {
      savePartialTask(updates);
      savePartialTaskTimerRef.current = null;
    }, delay);
  }

  async function clearPartialTask() {
    try {
      if (savePartialTaskTimerRef.current) {
        clearTimeout(savePartialTaskTimerRef.current);
        savePartialTaskTimerRef.current = null;
      }

      if (user?._id) {
        await deletePartialTask(user._id);
      }

      setHasPartialTask(false);
      window.localStorage.removeItem('partialTaskMinimized');
      document.dispatchEvent(new CustomEvent('reload:partialTask'));
    } catch (error) {
      console.error('Erro ao limpar task parcial:', error);
    }
  }

  async function handleSubmit() {
    sendPartialLog({ type: 'handleSubmit' });

    // Cancela qualquer timer de salvamento parcial pendente para evitar que seja salvo após o submit
    if (savePartialTaskTimerRef.current) {
      clearTimeout(savePartialTaskTimerRef.current);
      savePartialTaskTimerRef.current = null;
    }

    try {
      if (
        loading ||
        !message ||
        !resultMessage ||
        !userInfo ||
        !titleLength ||
        titleLength?.textLength < minTitleLength ||
        titleLength?.fullLength > maxTitleLength ||
        !messageLength ||
        messageLength?.textLength < minMessageLength ||
        messageLength?.fullLength > maxMessageLength
      )
        return;

      setLoading(true);

      let team: any = [];
      setbkpTarget(target);

      if (target) {
        team =
          target.map(item => {
            return {
              _id: item._id,
              cad_missao: item.extra?.cad_missao,
              cod_etapa: item.extra?.cod_etapa,
              funcao_localizacao: item.extra?.funcao_localizacao,
              message: item.message,
            };
          }) || [];
      }

      const distribution = validateDistribution(team);

      if (!distribution.valid) {
        setLoading(false);

        return toast.error(`Informe o percentual de participação de cada participante para enviar a realização.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }

      team = distribution.team;

      // const managerOnTeam = await checkManagerOnTeam(
      //   params.id && params.id !== 'cadastrar' ? currentTask.owner : user?._id,
      //   team.map((x: { _id: any }) => x._id),
      // );

      if (params.id && params.id !== 'cadastrar') {
        const actualTask = { ...currentTask };

        if (actualTask.status === 'rejeitado' && !!actualTask?.feedback?.length) {
          deleteMissionFeedback(actualTask._id);
        }

        team.forEach((obj: any) => {
          const currentTaskTeamUser = actualTask.team.find((x: { _id: any }) => x._id === obj._id);

          if (!!currentTaskTeamUser) {
            if (currentTaskTeamUser.cad_missao) {
              obj.cad_missao = currentTaskTeamUser.cad_missao;
            }

            if (currentTaskTeamUser.cod_etapa) {
              obj.cod_etapa = currentTaskTeamUser.cod_etapa;
            }

            if (currentTaskTeamUser.funcao_localizacao) {
              obj.funcao_localizacao = currentTaskTeamUser.funcao_localizacao;
            }
          }
        });

        // team.forEach((obj: any) => {
        //   const currentTaskTeamUser = currentTask.team.find((x: { _id: any }) => x._id === obj._id);

        //   if (!!currentTaskTeamUser?.requiresApproval) {
        //     obj.requiresApproval = currentTaskTeamUser.requiresApproval;

        //     if (typeof currentTaskTeamUser.approvedByManager !== 'undefined') {
        //       obj.approvedByManager = currentTaskTeamUser.approvedByManager;
        //     }
        //   } else {
        //     const managerOnTeamUser = managerOnTeam?.find(x => x.chave === obj._id);

        //     if (!!managerOnTeamUser && !!managerOnTeamUser.requiresApproval) {
        //       obj.requiresApproval = true;
        //     }
        //   }
        // });

        const totalParticipantes = team?.length;
        const participants_approval: any = {
          total: totalParticipantes,
        };

        // Teto já aprovado para esta realização (número de participantes que algum gestor — em tela
        // própria, na apreciação ou em edição anterior — autorizou). Uma vez aprovada, a aprovação
        // é PERSISTENTE: só é revista se a quantidade for aumentada ACIMA desse teto.
        const previouslyApprovedMax =
          currentTask?.participants_approval?.status === 'approved' && typeof currentTask?.maxPlayersNumber === 'number'
            ? currentTask.maxPlayersNumber
            : null;

        // A inclusão de participantes é livre, sem limite de quantidade. A faixa de participantes
        // define apenas o status de autorização:
        // - já aprovada e quantidade dentro do teto aprovado (decréscimo, igual ou acréscimo até o
        //   teto): mantém a aprovação para o número antigo;
        // - até 20: liberada automaticamente (autoApproved);
        // - acima de 20 e dentro do nível de quem edita (maxMembersByProfile): já aprovada;
        // - acima do nível de quem edita: pendente de autorização pelo nível correspondente.
        if (previouslyApprovedMax !== null && totalParticipantes <= previouslyApprovedMax) {
          participants_approval.status = 'approved';
          actualTask.maxPlayersNumber = previouslyApprovedMax;

          if (currentTask.participants_approval?.type) {
            participants_approval.type = currentTask.participants_approval.type;
          }

          if (currentTask.participants_approval?.values) {
            participants_approval.values = currentTask.participants_approval.values;
          }
        } else if (totalParticipantes <= maxMembersWithoutApproval) {
          participants_approval.status = 'autoApproved';
          delete actualTask.maxPlayersNumber;
        } else if (totalParticipantes <= maxMembersByProfile) {
          participants_approval.status = 'approved';
          actualTask.maxPlayersNumber = totalParticipantes;
        } else {
          participants_approval.status = 'pending';
          delete actualTask.maxPlayersNumber;

          const band = getParticipantsApprovalBand(totalParticipantes);
          if (band) {
            participants_approval.type = band.type;
            participants_approval.values = band.values;
          }
        }

        const data = await updateTask({
          ...actualTask,
          team,
          title,
          indicator: {
            nome: indicator?.label || undefined,
            cod: indicator?.value || undefined,
            bloco: (indicator as any)?.bloco || undefined,
            cod_bloco: (indicator as any)?.cod_bloco || undefined,
          },
          start: { $date: new Date(periodStart).getTime() },
          end: { $date: new Date(periodEnd).getTime() },
          nec: selectedNec?.value,
          description: message,
          status: userInfo.cod_etapa > 0 ? actualTask.status : 'aguardando',
          original_manager: initialManager._id,
          // Fluxo MAD (MOD-07): grava o parceiro da dupla como non_evaluator (array
          // vazio na opção solo). Fora do MAD o campo não é adicionado.
          ...(userInfo?.mad ? { non_evaluator: initialPartner?._id ? [initialPartner._id] : [] } : {}),
          result: resultMessage,
          participants_approval,
        });

        if (Array.isArray(data?.teamMemberError) && data?.teamMemberError.length) {
          setSubmitError(formatTeamMemberErrorMessage(data.teamMemberError));
        } else if (data?.teamMemberError) {
          setSubmitError(data.teamMemberError);
        }

        if (usersNotification?.length) {
          usersNotification.forEach((userId: string) => {
            const payload = {
              target: userId,
              user: userInfo?.chave,
              fase: currentCycle?._id,
              taskId: actualTask._id,
              created: { $date: new Date().getTime() },
            };

            sendNotificationTask(payload);
          });
        }
      } else {
        const serverTime = await getServerTime();

        if (
          !currentCycle?.status ||
          currentCycle.status !== 'open' ||
          serverTime > currentCycle.date_finish ||
          serverTime < currentCycle.date_start
        ) {
          return toast.error(`Não há fase disponível para envio de realizações.`, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }

        const data = await submitTask({
          owner: user?._id,
          owner_cad_missao: userInfo?.cad_missao,
          owner_cod_etapa: userInfo?.cod_etapa,
          owner_funcao_localizacao: userInfo?.funcao_localizacao,
          team,
          start: { $date: new Date(periodStart).getTime() },
          end: { $date: new Date(periodEnd).getTime() },
          nec: selectedNec.value,
          title,
          indicator: {
            nome: indicator?.label || undefined,
            cod: indicator?.value || undefined,
            bloco: (indicator as any)?.bloco || undefined,
            cod_bloco: (indicator as any)?.cod_bloco || undefined,
          },
          description: message,
          status: 'aguardando',
          original_manager: initialManager._id,
          // Fluxo MAD (MOD-07): grava o parceiro da dupla como non_evaluator (array
          // vazio na opção solo). Fora do MAD o campo não é adicionado.
          ...(userInfo?.mad ? { non_evaluator: initialPartner?._id ? [initialPartner._id] : [] } : {}),
          result: resultMessage,
        });

        if (!data._id && data?.message) {
          setSubmitError(data.message);
        } else if (Array.isArray(data?.teamMemberError) && data?.teamMemberError.length) {
          setSubmitError(formatTeamMemberErrorMessage(data.teamMemberError));
        } else if (data?.teamMemberError) {
          setSubmitError(data.teamMemberError);
        } else {
          if (usersNotification?.length) {
            usersNotification.forEach((userId: string) => {
              const payload = {
                target: userId,
                user: userInfo?.chave,
                fase: currentCycle?._id,
                taskId: data._id,
                created: { $date: new Date().getTime() },
              };

              sendNotificationTask(payload);
            });
          }
        }
      }

      setLoading(false);
      setSubmitted(true);
      refreshUser();

      window.localStorage.removeItem('partialTaskMinimized');
    } catch (e) {
      setLoading(false);
    }
  }

  function removerTagsP(texto: string): string {
    const regex = /<p[^>]*>|<\/p>|\n|&nbsp;/gi;
    const textoSemTagsP = texto.replace(regex, '');

    return textoSemTagsP;
  }

  function removeTarget(index: number) {
    if (target && target.length) {
      const newTargetList = [...target];
      const [removida] = newTargetList.splice(index, 1);
      setTarget(newTargetList);
      if (removida) esquecerPercentualDaUltimaVez([removida._id]);

      // Salva log parcial após remover um membro da equipe
      savePartialTask({ target: newTargetList });
    }
  }

  const extractKeys = (data: any) => {
    const keys = [];

    if (data) {
      keys.push(data.chave);

      Object.keys(data).forEach(key => {
        if (key.startsWith('gestor') && key !== 'gestor1') {
          keys.push(data[key].chave);
        }
      });
    }

    return keys;
  };

  function changeManager(item: User) {
    // No fluxo MAD (MOD-07) a dupla é escolhida explicitamente via changeManagerDupla,
    // então o bloqueio antigo (impedir indicar quem faz parte da dupla) não se aplica —
    // neutralizado só no caminho MAD com `!userInfo?.mad`. Não-MAD permanece idêntico.
    if (
      !userInfo?.mad &&
      userInfo?.mad_dupla &&
      Array.isArray(userInfo?.mad_dupla) &&
      userInfo?.mad_dupla?.find((e: any) => e._id === item._id)
    ) {
      setShowModalAlert('Esse Gestor não pode ser indicado porque ele já faz parte da Dupla Liderança.');

      sendPartialLog({
        type: 'setInitialManager',
        data: item._id || 'ND',
      });

      return;
    }

    sendPartialLog({
      type: 'setInitialManager',
      data: item._id || 'ND',
    });

    setInitialManager(item);
    setSearchResultsManager([]);
    setSearchManager('');

    // Salva log parcial após alterar o gestor responsável
    savePartialTask({ initialManager: item });
  }

  // Seleção de uma opção de dupla no fluxo MAD (MOD-07): grava o gestor como
  // responsável e o parceiro (quando houver) como non_evaluator. Espelha o
  // changeManager, mas ciente da dupla e sem o bloqueio antigo — a dupla é escolhida
  // explicitamente. Nunca é acionado no cadastro não-MAD (só a lista de duplas o usa).
  function changeManagerDupla(option: DuplaOption) {
    const gestor = option.gestor;
    const parceiro = option.parceiro ?? undefined;

    sendPartialLog({
      type: 'setInitialManager',
      data: gestor?._id || 'ND',
    });

    setInitialManager(gestor);
    setInitialPartner(parceiro);
    setSearchResultsDupla([]);
    setSearchManager('');

    // Salva log parcial após alterar a dupla responsável.
    savePartialTask({ initialManager: gestor, initialPartner: parceiro });
  }

  // Efeitos de hoje das recusas por limite e por preferência, para uma pessoa (busca) ou várias (colagem).
  function registrarEfeitosDasRecusas(recusas: { pessoa: User; efeito?: 'limite' | 'preferencia' }[]) {
    const comLimite = recusas
      .filter(recusa => recusa.efeito === 'limite' && !usersNotification?.includes(recusa.pessoa._id))
      .map(recusa => recusa.pessoa._id);

    if (comLimite.length) {
      setUsersNotification([...usersNotification, ...comLimite]);
    }

    const comPreferencia = recusas
      .filter(recusa => recusa.efeito === 'preferencia' && !usersNotificationPreference?.includes(recusa.pessoa._id))
      .map(recusa => recusa.pessoa._id);

    if (comPreferencia.length) {
      setUsersNotificationPreference([...usersNotificationPreference, ...comPreferencia]);

      comPreferencia.forEach(chave =>
        sendPreferenceNotification({
          target: chave,
          user: userInfo?.chave,
          item: 'task',
          created: { $date: new Date().getTime() },
        }),
      );
    }
  }

  function contextoDaInclusao(participantes: User[]): ContextoDaInclusao {
    return {
      participantes,
      gestoresDoAutor: userInfo?.gestores,
      gestoresDaTarefa: extractKeys(currentTask?.gestores),
    };
  }

  async function addTarget(item: User) {
    const avaliacao = avaliarInclusao(item, contextoDaInclusao(target ?? []));

    focusOnElement('.currentAction input');

    if (!avaliacao.aceita) {
      registrarEfeitosDasRecusas([{ pessoa: item, efeito: avaliacao.efeito }]);
      setShowModalAlert(avaliacao.motivo);
      return;
    }

    const newTargetList = target ? [...target, item] : [item];
    setTarget(newTargetList);
    setSearchResults([]);
    setSearching(false);
    if (searchRef && searchRef.current) {
      searchRef.current.value = '';
    }
    if (teamApproval(newTargetList).showAlert) {
      setOpenTargetAlert(true);
    }

    // Salva log parcial após adicionar um membro à equipe
    savePartialTask({ target: newTargetList });
  }

  /**
   * Inclui várias pessoas já consultadas, pelas regras da busca (RCP-14, SUG-12): acrescenta as aceitas no fim, na ordem
   * recebida, registra os efeitos das recusas, abre a alçada e grava o rascunho uma vez. `percentuais` pré-preenche o
   * percentual de quem for aceito (SUG-19).
   */
  function incluirPessoas(pessoas: User[], percentuais: Record<string, string> = {}) {
    const participantes = target ?? [];
    const aceitas: User[] = [];
    const recusas: { pessoa: User; motivo: string; efeito?: 'limite' | 'preferencia' }[] = [];

    for (const pessoa of pessoas) {
      const avaliacao = avaliarInclusao(pessoa, contextoDaInclusao([...participantes, ...aceitas]));

      if (avaliacao.aceita) {
        aceitas.push(pessoa);
      } else {
        recusas.push({ pessoa, motivo: avaliacao.motivo, efeito: avaliacao.efeito });
      }
    }

    registrarEfeitosDasRecusas(recusas);

    if (aceitas.length) {
      const novaLista = [...participantes, ...aceitas];
      const percentuaisDasAceitas = aceitas.filter(pessoa => percentuais[pessoa._id]).map(pessoa => [pessoa._id, percentuais[pessoa._id]]);
      const novaDistribuicao = { ...teamDistribution, ...Object.fromEntries(percentuaisDasAceitas) };

      setTarget(novaLista);
      if (percentuaisDasAceitas.length) setTeamDistribution(novaDistribuicao);
      if (teamApproval(novaLista).showAlert) {
        setOpenTargetAlert(true);
      }
      savePartialTask({ target: novaLista, teamDistribution: novaDistribuicao });
    }

    return { aceitas, recusas };
  }

  // A colagem só inclui depois da consulta; chamar a versão da última renderização evita sobrescrever o que o autor
  // mudou enquanto isso (pessoas, percentuais, rascunho) com a lista do momento do clique (RCP-15).
  const incluirPessoasDaUltimaRenderizacao = useRef(incluirPessoas);
  incluirPessoasDaUltimaRenderizacao.current = incluirPessoas;

  /**
   * Inclui de uma vez as chaves coladas (RCP-12 a RCP-21): descarta quem já está na lista, consulta como a busca,
   * aplica as mesmas regras de inclusão e grava o rascunho uma vez. As recusas vão para o resumo em vez do alerta.
   */
  async function incluirVariasChaves(
    chaves: string[],
    onProgresso: (feitas: number, total: number) => void,
  ): Promise<ResumoDaColagem> {
    sendPartialLog({ type: 'bulkAddTarget', data: chaves });

    const jaNaLista = new Set([...(target ?? []).map(pessoa => pessoa._id), user?._id]);
    const fase = idDaFaseDaBusca();
    const resultados = await consultarEmLotes(
      chaves.filter(chave => !jaNaLista.has(chave)),
      chave => findPlayer(chave, true, true, true, false, fase),
      { onProgresso },
    );

    const encontradas: User[] = [];
    const recusas: RecusaDaColagem[] = [];
    const chavesComFalha: string[] = [];

    for (const resultado of resultados) {
      const { chave } = resultado;

      if ('erro' in resultado) {
        recusas.push({ chave, motivo: 'Não foi possível consultar' });
        chavesComFalha.push(chave);
        continue;
      }

      const pessoa: User | undefined = Array.isArray(resultado.pessoas)
        ? resultado.pessoas.find((encontrada: User) => encontrada._id === chave)
        : undefined;

      if (pessoa) {
        encontradas.push(pessoa);
      } else {
        recusas.push({ chave, motivo: 'Matrícula não encontrada na base do Joga Junto' });
      }
    }

    const inclusao = incluirPessoasDaUltimaRenderizacao.current(encontradas);
    recusas.push(...inclusao.recusas.map(({ pessoa, motivo }) => ({ chave: pessoa._id, motivo })));
    // O resumo segue a ordem do texto colado, juntando as duas fontes de recusa.
    recusas.sort((a, b) => chaves.indexOf(a.chave) - chaves.indexOf(b.chave));

    return { incluidos: inclusao.aceitas.length, recusas, chavesComFalha };
  }

  async function carregarSugestoes(autor: string, taskId?: string) {
    try {
      const pessoas = await buscarSugestoesDeParticipantes(taskId);
      setSugestoes({ tipo: 'pronto', secoes: montarSecoes(pessoas, autor) });
    } catch (erro) {
      setSugestoes({ tipo: 'erro' });
    }
  }

  /** Inclui as marcadas no painel de sugestões; quem vem do histórico chega com o percentual da última vez (SUG-12, SUG-19). */
  function incluirSugeridas(pessoas: PessoaSugerida[]) {
    const percentuais: Record<string, string> = {};
    pessoas.forEach(pessoa => {
      const percentual = pessoa.origem === 'historico' ? percentualDaUltimaVez(pessoa.ultimoPercentual) : undefined;
      if (percentual) percentuais[pessoa._id] = percentual;
    });

    const { aceitas, recusas } = incluirPessoas(pessoas, percentuais);
    const origemDe = new Map(pessoas.map(pessoa => [pessoa._id, pessoa.origem]));

    // O log registra inclusões (SUG-13): se todas foram recusadas, não há o que registrar.
    if (aceitas.length) {
      sendPartialLog({ type: 'addSuggested', data: aceitas.map(pessoa => ({ chave: pessoa._id, origem: origemDe.get(pessoa._id) })) });
    }
    setComPercentualDaUltimaVez(atuais => {
      const proximas = new Set(atuais);
      aceitas.filter(pessoa => percentuais[pessoa._id]).forEach(pessoa => proximas.add(pessoa._id));
      return proximas;
    });

    if (recusas.length) {
      setShowModalAlert(recusas[0].motivo);
    }
  }

  function esquecerPercentualDaUltimaVez(chaves: string[]) {
    setComPercentualDaUltimaVez(atuais => {
      if (!chaves.some(chave => atuais.has(chave))) return atuais;
      const proximas = new Set(atuais);
      chaves.forEach(chave => proximas.delete(chave));
      return proximas;
    });
  }

  function reset() {
    setTarget(undefined);
    setSubmitted(false);
    setLoading(false);
    setSubmitError(undefined);
    setSearchResults([]);
    setSearching(false);
    setMessage(undefined);
    setResultMessage(undefined);
    setTitle(undefined);
    setPeriodStart(undefined);
    setPeriodEnd(undefined);
    setNec(undefined);
    setIndicator(undefined);
    setStep(primeiraEtapa());
    setTeamDistribution(undefined);
  }

  async function restoreDefaultTargetAndManager() {
    if (!user?._id || !userInfo) return;

    try {
      const result = await findPlayer(user._id, true);
      setTarget(result);
    } catch (error) {
      console.error('Erro ao restaurar participante padrão:', error);
    }

    try {
      const gestorCode = userInfo?.gestor?.padStart ? userInfo.gestor.padStart(7, '0') : null;
      if (gestorCode) {
        const gestorResult = await getPlayerInfo(`F${gestorCode}`);
        if (gestorResult) {
          setInitialManager(gestorResult);
          setInitialPartner(deriveInitialPartner(!!userInfo?.mad, gestorResult));
        }
      }
    } catch (error) {
      console.error('Erro ao restaurar gestor padrão:', error);
    }
  }

  async function handleClearDraft() {
    sendPartialLog({ type: 'clearDraft', location: 'bottom' });
    // Remonta o formulário: o editor do nome está na primeira etapa e guardaria o texto apagado.
    setCargaInicialConcluida(false);

    await clearPartialTask();

    reset();
    setTitleLength(undefined);
    setMessageLength(undefined);
    setResultLength(undefined);
    setSelectedNec([]);
    setDemandanteValue([]);
    setShowDemandante(false);
    setTeamDistribution({});
    setSearchManager('');
    setSearchResultsManager([]);
    setOpenTargetAlert(false);
    setIsEmpty(false);
    setIsEmptyName(false);
    setIsAlreadySelectedInList(false);
    setUsersNotification([]);
    setUsersNotificationPreference([]);
    setComPercentualDaUltimaVez(new Set());

    await restoreDefaultTargetAndManager();
    setCargaInicialConcluida(true);
  }

  function handleCloseModal() {
    reset();
    navigate('../', { state: { direction: 'back' } });
    // lastFocusedElementRef.current?.focus();
    // document.removeEventListener('keydown', trapFocus);
  }

  const handleDemandanteChange = (e: any) => {
    if (selectedNec?.value?.cd_alcc === '123456789') {
      setDemandanteValue(e);
      selectedNec.value.nm_uor_sltt = e.value;
      // Congela o código do UOR no documento. O scheduler do relatório resolve o
      // código por um $lookup por NOME na nec__c, que é apagada e recarregada
      // inteira: quando um nome sai do feed, realizações antigas passam a exibir
      // '---'. Com o código gravado aqui, o relatório para de degradar.
      selectedNec.value.cd_uor_sltt = e.cd_uor_sltt;
    }

    // Salva log parcial após alterar o demandante
    savePartialTask({ demandanteValue: e });
  };

  function validateInput(e: any) {
    if (e.value > 100) {
      e.value = 100;
    } else if (e.value < 0) {
      e.value = 0;
    } else if (e.value === '') {
      e.value = 0;
    }

    const teamMember = e.id.replace('team_player_', '');
    const newTeamDistribution = { ...teamDistribution, [teamMember]: e.value };

    setTeamDistribution(newTeamDistribution);

    // A escolha do percentual esvazia a descrição de atuação desse membro, em qualquer faixa da
    // escala: 25% e 50% porque descrever ali é dispensado, 75% e 100% porque exigem redação
    // própria. Vale inclusive sobre texto já digitado. Percentual fora da escala (rascunho
    // legado) não mexe no campo.
    const newTarget =
      dispensesDescription(e.value) || requiresOwnDescription(e.value)
        ? (target || []).map(item =>
            item._id === teamMember ? { ...item, message: '', messageLength: { textLength: 0, fullLength: 0 } } : item,
          )
        : target;

    setTarget(newTarget);

    // Salva log parcial após alterar o percentual de participação (com debounce para evitar envios durante digitação).
    // Percentual e descrição vão na mesma chamada: separá-las faria a segunda ler o estado antigo do closure.
    debouncedSavePartialTask({ teamDistribution: newTeamDistribution, target: newTarget });
  }

  const isValidDistribution: boolean = useMemo((): boolean => {
    if (!target?.length || !Object.keys(teamDistribution).length) return false;

    for (let index = 0; index < target.length; index++) {
      const teamItem = teamDistribution[target[index]._id];

      if (!teamItem || isNaN(Number(teamItem)) || Number(teamItem) <= 0) {
        return false;
      }
    }

    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, teamDistribution, searchResults]);

  // A validação depende do percentual de cada participante desde que 25% e 50% passaram a
  // dispensar a descrição — por isso `teamDistribution` entrou nas dependências.
  const isValidDistributionMessage = useMemo(
    () => validateParticipationMessages(target, teamDistribution, { min: minMessageTarget, max: maxMessageTarget }),
    [target, teamDistribution],
  );

  /**
   * Aplica o percentual informado a cada participante.
   *
   * Devolve `valid` em vez de cair para o `team` cru: a versão anterior, quando
   * algum percentual faltava, retornava silenciosamente o `team` sem o campo
   * `percent` e o cadastro seguia — o participante chegava ao banco incompleto.
   */
  function validateDistribution(team: any): { valid: boolean; team: any[] } {
    if (!team?.length) {
      return { valid: true, team: [] };
    }

    let valid = true;

    const distribution = team.map((item: any) => {
      const teamInput: any = teamDistribution[item._id] || 0;

      if (isNaN(teamInput) || teamInput > 100 || teamInput <= 0) {
        valid = false;
      }

      return { ...item, percent: parseInt(teamInput) };
    });

    return { valid, team: distribution };
  }

  function getUserDistribution(_id: string) {
    try {
      return teamDistribution[_id];
    } catch (e) {
      return 0;
    }
  }

  function handleChangeTitle(content: string, contentLength: any) {
    const newTitle = removerTagsP(content);
    setTitle(newTitle);
    setTitleLength(contentLength);

    // Salva log parcial após alterar o título (com debounce para evitar envios durante digitação)
    debouncedSavePartialTask({ title: newTitle });
  }

  function handleChangeIndicator(e: any) {
    setIndicator(e);

    // Salva log parcial após alterar o indicador
    savePartialTask({ indicator: e });
  }

  function handleChangePeriodStart(e: any) {
    const newPeriodStart = format(utcToZonedTime(e.target.value, 'GMT'), 'yyyy-MM-dd');
    setPeriodStart(newPeriodStart);

    // Salva log parcial após alterar a data de início
    savePartialTask({ periodStart: newPeriodStart });
  }

  function handleChangePeriodEnd(e: any) {
    const newPeriodEnd = format(utcToZonedTime(e.target.value, 'GMT'), 'yyyy-MM-dd');
    setPeriodEnd(newPeriodEnd);

    // Salva log parcial após alterar a data de conclusão
    savePartialTask({ periodEnd: newPeriodEnd });
  }

  function handleChangeMessage(content: string, contentLength: any) {
    const newMessage = removerTagsP(content);
    setMessage(newMessage);
    setMessageLength(contentLength);

    // Salva log parcial após alterar a descrição da realização (com debounce para evitar envios durante digitação)
    debouncedSavePartialTask({ message: newMessage });
  }

  function handleChangeResult(content: string, contentLength: any) {
    const newResultMessage = removerTagsP(content);
    setResultMessage(newResultMessage);
    setResultLength(contentLength);

    // Salva log parcial após alterar o resultado da realização (com debounce para evitar envios durante digitação)
    debouncedSavePartialTask({ resultMessage: newResultMessage });
  }

  function handleChangeMessageTarget(content: string, contentLength: any, index: number) {
    const newTarget = target ? [...target] : [];
    newTarget[index].message = content;
    newTarget[index].messageLength = contentLength;
    setTarget(newTarget);

    // Salva log parcial após alterar a descrição de atuação do membro (com debounce para evitar envios durante digitação)
    debouncedSavePartialTask({ target: newTarget });
  }

  async function loadNecs(nec?: string) {
    const necs = await getNecs(nec);
    setNecsList(necs);

    return necs;
  }

  const defineSelectedNec = useCallback(async (result: any) => {
    if (typeof result.nec === 'string') {
      const nec = await loadNec(result.nec);
      if (nec) {
        setDemandanteValue(nec.nm_uor_sltt);
        return {
          label: nec.cd_alcc + ' - ' + nec.nm_alcc,
          value: nec,
        };
      } else {
        return {
          label: '123456789 - SEM NEC/INICIATIVA VINCULADA',
          value: { cd_alcc: '123456789', nm_alcc: 'SEM NEC/INICIATIVA VINCULADA' },
        };
      }
    } else if (result.nec) {
      return {
        label: result.nec.cd_alcc + ' - ' + result.nec.nm_alcc,
        value: result.nec,
      };
    } else {
      return undefined;
    }
  }, []);

  useEffect(() => {
    async function loadData() {
      const result = await getTaskById(params.id);

      if (result && userInfo) {
        setCurrentTask(result);
        setEditing(true);

        const resp = await getPlayerInfo('F' + result?.original_manager?.padStart(7, '0'));
        if (resp) {
          setInitialManager(resp);
        }

        // Restaura o parceiro da dupla apenas no fluxo MAD (MOD-07) para preservar
        // non_evaluator ao reeditar; fora do MAD não há non_evaluator de dupla.
        if (userInfo?.mad && Array.isArray(result?.non_evaluator) && result.non_evaluator.length) {
          const partnerData = await getPlayerInfo('F' + String(result.non_evaluator[0]).replace('F', '').padStart(7, '0'));
          if (partnerData) {
            setInitialPartner(partnerData);
          }
        }

        if (!!result?.maxPlayersNumber) {
          setMaxMembers(result.maxPlayersNumber as number);
        }

        const listLinkedIndicators = await getLinkedIndicators(result.owner);

        if (listLinkedIndicators?.indicadores?.length) {
          setIndicatorsList(listLinkedIndicators.indicadores);
        }
        loadVerifyTask(listLinkedIndicators?.indicadores);

        const isOwnerOnTeam: any = result.team.find((item: any) => item._id === user?._id);

        if (!isOwnerOnTeam && userInfo.cad_missao > 0) {
          result.target.push({
            _id: user?._id,
            name: user?.name,
            extra: user?.extra,
          });
          result.team.push({
            _id: user?._id,
          });
        }

        const currentTeam = result.target.sort((a: any, b: any) => (a._id === user?._id ? -1 : 1)); // = result.target.filter((item:any)=>item._id !== user?._id);
        const currentDistribution = result.team.reduce((value: any, item: any) => {
          value[item._id] = item.percent;
          return value;
        }, {});

        currentTeam.map((item: any) => {
          item.message = result?.team?.find((x: any) => x._id === item._id)?.message;
          return item;
        });

        setTarget(currentTeam);
        setTeamDistribution(currentDistribution);
        setMessage(result.description);
        setResultMessage(result.result);
        setTitle(result.title);
        setDemandanteValue(
          result.nec && result.nec.cd_alcc === '123456789'
            ? {
                label: result.nec.nm_uor_sltt,
                value: result.nec.nm_uor_sltt,
                // Código congelado no cadastro: marca só a UOR escolhida quando há outra de mesmo nome.
                cd_uor_sltt: result.nec.cd_uor_sltt,
              }
            : result.nec && result.nec.cd_alcc !== '123456789'
              ? result.nec.nm_uor_sltt
              : undefined,
        );
        const selectNec = await defineSelectedNec(result);
        setSelectedNec(selectNec);
        setIndicator(
          result.indicator
            ? {
                label: result.indicator.nome,
                value: result.indicator.cod,
                bloco: result.indicator.bloco,
                cod_bloco: result.indicator.cod_bloco || result.indicator.codBloco,
              }
            : undefined,
        );

        try {
          setPeriodStart(format(utcToZonedTime(result.start, 'GMT'), 'yyyy-MM-dd'));
          setPeriodEnd(format(utcToZonedTime(result.end, 'GMT'), 'yyyy-MM-dd'));
        } catch (e) {
          console.log(e);
        }

        const date = new Date(result.created);

        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);

        setMaxDate(format(lastDay, 'yyyy-MM-dd'));
        setMinDate(format(firstDay, 'yyyy-MM-dd'));
      }

      setLoading(false);
    }

    async function loadUors() {
      try {
        const uors = await getUors(userInfo?.uor_trabalho);

        setNeedNec(uors);
        setNecCheckFailed(false);
      } catch (e) {
        // Sem saber se o UOR exige NEC, o cadastro não pode seguir: liberá-lo
        // aqui é o que grava realizações sem demandante.
        setNeedNec([]);
        setNecCheckFailed(true);
      }
    }

    async function loadDemandates() {
      const uors = await getUorDemandantes();
      setDemandanteList(buildDemandanteOptions(uors));
    }

    async function loadUserBySearch() {
      const listLinkedIndicators = await getLinkedIndicators();

      if (listLinkedIndicators?.indicadores?.length) {
        setIndicatorsList(listLinkedIndicators.indicadores);
      }

      loadVerifyTask(listLinkedIndicators?.indicadores);

      const currentTime = await getServerTime();

      const date = new Date(currentTime);

      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);

      setMaxDate(format(lastDay, 'yyyy-MM-dd'));
      setMinDate(format(firstDay, 'yyyy-MM-dd'));

      // Verifica se existe uma task parcial salva
      const partialTask = await getPartialTask();

      if (partialTask) {
        setHasPartialTask(true);
        isLoadingPartialTask = true;

        try {
          const savedTask = partialTask;

          if (savedTask.team && Array.isArray(savedTask.team) && savedTask.team.length) {
            const teamPromises = savedTask.team.map(async (member: any) => {
              const playerData = await findPlayer(member._id, true);

              if (playerData && playerData.length) {
                const player = playerData[0];
                player.message = member.message;
                return player;
              }

              return null;
            });

            const teamResults = await Promise.all(teamPromises);
            const validTeam = teamResults.filter((member: any) => member !== null);

            if (validTeam.length) {
              setTarget(validTeam);

              const distribution: any = {};
              savedTask.team.forEach((member: any) => {
                if (member.percent) {
                  distribution[member._id] = member.percent;
                }
              });
              if (Object.keys(distribution).length) {
                setTeamDistribution(distribution);
              }
            } else {
              const result = await findPlayer(user?._id, true);
              setTarget(result);
            }
          } else {
            const result = await findPlayer(user?._id, true);
            setTarget(result);
          }

          if (savedTask.title) {
            setTitle(savedTask.title);
            setTitleLength({
              textLength: savedTask.title
                .replace(/(\n|&nbsp;)/gi, ' ')
                .replace(/\s+/gi, ' ')
                .trim().length,
              fullLength: savedTask.title
                .replace(/(\n|&nbsp;)/gi, ' ')
                .replace(/\s+/gi, ' ')
                .trim().length,
            });
          }

          if (savedTask.indicator && savedTask.indicator.cod) {
            setIndicator({
              label: savedTask.indicator.nome,
              value: savedTask.indicator.cod,
              bloco: savedTask.indicator.bloco,
              cod_bloco: savedTask.indicator.cod_bloco || savedTask.indicator.codBloco,
            });
          }

          if (savedTask.start) {
            try {
              setPeriodStart(format(utcToZonedTime(savedTask.start, 'GMT'), 'yyyy-MM-dd'));
            } catch (e) {
              console.log('Erro ao carregar data de início:', e);
            }
          }

          if (savedTask.end) {
            try {
              setPeriodEnd(format(utcToZonedTime(savedTask.end, 'GMT'), 'yyyy-MM-dd'));
            } catch (e) {
              console.log('Erro ao carregar data de fim:', e);
            }
          }

          if (savedTask.nec) {
            const necValue = savedTask.nec;
            setSelectedNec({
              label: necValue.cd_alcc + ' - ' + necValue.nm_alcc,
              value: necValue,
            });

            if (necValue.cd_alcc === '123456789') {
              setShowDemandante(true);
            }
          }

          if (savedTask.demandante) {
            if (savedTask.nec && savedTask.nec.cd_alcc === '123456789') {
              setDemandanteValue({
                label: savedTask.demandante,
                value: savedTask.demandante,
                cd_uor_sltt: savedTask.nec.cd_uor_sltt,
              });
            } else {
              setDemandanteValue(savedTask.demandante);
            }
          }

          if (savedTask.original_manager) {
            const managerData = await getPlayerInfo('F' + savedTask.original_manager.replace('F', '').padStart(7, '0'));

            if (managerData) {
              setInitialManager(managerData);
            }
          }

          // Restaura o parceiro da dupla apenas no fluxo MAD (MOD-07) ao retomar um
          // rascunho; fora do MAD não há non_evaluator de dupla no rascunho.
          if (userInfo?.mad && Array.isArray(savedTask.non_evaluator) && savedTask.non_evaluator.length) {
            const partnerData = await getPlayerInfo('F' + String(savedTask.non_evaluator[0]).replace('F', '').padStart(7, '0'));

            if (partnerData) {
              setInitialPartner(partnerData);
            }
          }

          if (savedTask.description) {
            setMessage(savedTask.description);
            setMessageLength({
              textLength: savedTask.description
                .replace(/(\n|&nbsp;)/gi, ' ')
                .replace(/\s+/gi, ' ')
                .trim().length,
              fullLength: savedTask.description
                .replace(/(\n|&nbsp;)/gi, ' ')
                .replace(/\s+/gi, ' ')
                .trim().length,
            });
          }

          if (savedTask.result) {
            setResultMessage(savedTask.result);
            setResultLength({
              textLength: savedTask.result
                .replace(/(\n|&nbsp;)/gi, ' ')
                .replace(/\s+/gi, ' ')
                .trim().length,
              fullLength: savedTask.result
                .replace(/(\n|&nbsp;)/gi, ' ')
                .replace(/\s+/gi, ' ')
                .trim().length,
            });
          }

          setStep(etapaAoRetomar(savedTask.step));
        } catch (error) {
          console.error('Erro ao carregar task parcial:', error);
          // Em caso de erro, carrega apenas o usuário atual
          const result = await findPlayer(user?._id, true);
          setTarget(result);
        } finally {
          isLoadingPartialTask = false;
        }
      } else {
        setHasPartialTask(false);
        const result = await findPlayer(user?._id, true);
        setTarget(result);

        const gestorResult = await getPlayerInfo('F' + userInfo?.gestor?.padStart(7, '0'));
        if (gestorResult) {
          setInitialManager(gestorResult);
          setInitialPartner(deriveInitialPartner(!!userInfo?.mad, gestorResult));
        }
      }
    }

    async function loadVerifyTask(indicadores?: LinkedIndicators_Indicadores[]) {
      const data = await verifyTask(params.id);

      if (data && data?.total && data?.total > 0 && indicadores?.length && indicadores?.length > 1) {
        setHasOtherType(true);
      }
    }

    if (user && userInfo) {
      const cargaInicial = params && params.id && params.id !== 'cadastrar' ? loadData() : loadUserBySearch();
      cargaInicial.finally(() => setCargaInicialConcluida(true));

      loadNecs();
      loadUors();
      loadDemandates();
    }
  }, [defineSelectedNec, params, user, userInfo]);

  const periodStartIsValid: boolean = useMemo((): boolean => isPeriodStartValid(periodStart), [periodStart]);

  const periodEndIsValid: boolean = useMemo(
    (): boolean => isPeriodEndValid(periodStart, periodEnd, maxDate),
    [periodStart, periodEnd, maxDate],
  );

  const loadNec = async (necId: string) => {
    let necByLoad: any = await getNecs(necId);
    return necByLoad.length > 0 ? necByLoad[0] : null;
  };

  const dotsText = [
    'Até 20: Liberação automática;',
    'De 21 a 30: 3º nível gerencial de UE ou 2º nível de UT, UA ou CA;',
    'De 31 a 50: 2º nível gerencial de UE ou 1º nível de UT, UA ou CA;',
    'De 51 a 100: Diretor ou Gerente Geral UE;',
    'Acima de 100: Vice-Presidente.',
  ];

  function textRecomendation() {
    return (
      <ul className="m-0 mt-3 list-disc space-y-1 pl-5 text-xs text-content">
        {dotsText.map((text, index) => (
          <li key={text}>{text}</li>
        ))}
      </ul>
    );
  }

  let searchTimer: ReturnType<typeof setTimeout>;

  const timeoutDelay = 500;

  const handleInputChange = (e: any) => {
    if (searchTimer) {
      clearTimeout(searchTimer);
    }

    searchTimer = setTimeout(() => {
      loadNecs(e);
    }, timeoutDelay);
  };
  const missingNec = needNec.length > 0 && !selectedNec?.value;
  const missingDemandante = needNec.length > 0 && !!selectedNec?.value && !isDemandanteFilled(demandanteValue);

  /** Validações de hoje de cada etapa lógica (RCP-04). */
  const etapaInvalida = (etapa: number) => {
    return (
      (etapa === 0 && (!target?.length || !isValidDistribution || !isValidDistributionMessage.valid)) ||
      (etapa === 1 &&
        (!title?.length ||
          !indicator ||
          !indicator?.label ||
          !indicator?.value ||
          !periodStart?.length ||
          !periodEnd?.length ||
          !periodStartIsValid ||
          !periodEndIsValid ||
          !initialManager?._id ||
          new Date(periodEnd) < new Date(periodStart) ||
          !titleLength ||
          titleLength?.textLength < minTitleLength ||
          titleLength?.fullLength > maxTitleLength ||
          // (hasOtherType && indicator?.value === 1) ||
          necCheckFailed ||
          missingNec ||
          missingDemandante)) ||
      (etapa === 2 &&
        (necCheckFailed ||
          missingNec ||
          missingDemandante ||
          !message ||
          !resultMessage ||
          !messageLength ||
          messageLength?.textLength < minMessageLength ||
          messageLength?.fullLength > maxMessageLength ||
          !resultLength ||
          resultLength?.textLength < minResultLength ||
          resultLength?.fullLength > maxResultLength))
    );
  };

  const buttonContinueIsDisabled = () => etapaInvalida(step);

  // Participantes é a última etapa na tela: um rascunho ou uma edição não pode finalizar sem as outras válidas (RCP-05).
  const finalizarDesabilitado = () => ORDEM_DE_EXIBICAO.some(etapaInvalida);

  const etapaParaVoltar = etapaAnteriorNaTela(step);
  const etapaParaContinuar = proximaEtapa(step);

  // O percentual entra como texto, como entrava pelo campo oculto de antes: é assim que o rascunho o grava.
  function escolherPercentual(chave: string, valor: number) {
    sendPartialLog({ type: 'changeValue', data: { id: `team_player_${chave}`, value: valor } });
    esquecerPercentualDaUltimaVez([chave]);
    validateInput({ id: `team_player_${chave}`, value: String(valor) });
  }

  /** Mesmo efeito da escolha individual, para várias pessoas, com uma gravação de rascunho só (RCP-26). */
  function aplicarPercentualEmMassa(chaves: string[], valor: number) {
    sendPartialLog({ type: 'bulkChangeValue', data: { chaves, value: valor } });
    esquecerPercentualDaUltimaVez(chaves);

    const newTeamDistribution = { ...teamDistribution, ...Object.fromEntries(chaves.map(chave => [chave, String(valor)])) };
    const newTarget = (target || []).map(item =>
      chaves.includes(item._id) ? { ...item, message: '', messageLength: { textLength: 0, fullLength: 0 } } : item,
    );

    setTeamDistribution(newTeamDistribution);
    setTarget(newTarget);
    debouncedSavePartialTask({ teamDistribution: newTeamDistribution, target: newTarget });
  }

  const focarAoMontar = useCallback((elemento: HTMLElement | null) => elemento?.focus(), []);

  // Sugestões carregam uma vez, depois da carga inicial, sem segurar a etapa (SUG-04).
  useEffect(() => {
    if (!cargaInicialConcluida || !user?._id || sugestoesPedidas.current) return;
    sugestoesPedidas.current = true;
    // Na edição, o gestor que aprecia também edita: as sugestões e o autor que sai das seções são os do dono.
    const editando = !!params?.id && params.id !== 'cadastrar';
    carregarSugestoes(editando ? currentTask?.owner || user._id : user._id, editando ? params.id : undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cargaInicialConcluida, user?._id]);

  // Toda troca de etapa leva o foco ao título dela, inclusive a volta para a primeira (o "Voltar" some ali).
  const etapaAnterior = useRef(step);
  useEffect(() => {
    if (step !== etapaAnterior.current) {
      toNextStepFocus();
    }
    etapaAnterior.current = step;

    setAriaLabel(`Etapa ${posicaoNaTela(step) + 1} de 3 para cadastrar realização destaque`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Cleanup do timer de salvamento parcial quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (savePartialTaskTimerRef.current) {
        clearTimeout(savePartialTaskTimerRef.current);
        savePartialTaskTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    async function playerCanView() {
      const serverTime = await getServerTime();

      if (
        !currentCycle?.status ||
        currentCycle.status !== 'open' ||
        serverTime > currentCycle.date_finish ||
        serverTime < currentCycle.date_start
      ) {
        toast.error(`Não há fase disponível para envio de realizações.`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });

        return navigate('../');
      }
    }

    if (!!currentCycle && params && params.id && params.id === 'cadastrar') {
      playerCanView();
    }
  }, [currentCycle, navigate, params]);

  if (!userInfo) return null;

  const handleChangeNec = (selectedOption: any) => {
    setSelectedNec(selectedOption);

    let newDemandanteValue: any;
    if (selectedOption.value.cd_alcc === '123456789') {
      setShowDemandante(true);
      setDemandanteValue('');
      newDemandanteValue = '';
    } else {
      setShowDemandante(false);
      setDemandanteValue(selectedOption.value.nm_uor_sltt);
      newDemandanteValue = selectedOption.value.nm_uor_sltt;
    }

    // Salva log parcial após alterar a NEC
    savePartialTask({ selectedNec: selectedOption, demandanteValue: newDemandanteValue });
  };

  if (
    params &&
    params.id &&
    params.id === 'cadastrar' &&
    (userInfo?.cad_missao <= 0 ||
      (Array.isArray(userInfo?.mad_dupla) &&
        userInfo?.mad_dupla?.some((md: any) => typeof md.cad_missao === 'number' && md.cad_missao <= 0)))
  ) {
    return null;
  }

  if (!!userInfo.visualizador) {
    return null;
  }

  function focusOnElement(selector: string) {
    const element: any = document.querySelector(selector);
    if (!!element) {
      element?.focus();
    }
  }

  // function afterOpenModal() {
  //   lastFocusedElementRef.current = document.activeElement as HTMLElement;
  //   const focusableElementsString =
  //     'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex], [contenteditable]';
  //   const focusableElements = modalRef.current?.querySelectorAll(focusableElementsString);
  //   const firstFocusableElement = focusableElements ? (focusableElements[0] as HTMLElement) : null;
  //   firstFocusableElement?.focus();

  //   document.addEventListener('keydown', trapFocus);
  // }

  // function trapFocus(event: KeyboardEvent) {
  //   const focusableElementsString =
  //     'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable]';
  //   const focusableElements = modalRef.current?.querySelectorAll(focusableElementsString);
  //   if (focusableElements) {
  //     const firstFocusableElement = focusableElements[0] as HTMLElement;
  //     const lastFocusableElement = focusableElements[focusableElements.length - 1] as HTMLElement;

  //     if (event.key === 'Tab') {
  //       if (event.shiftKey) {
  //         if (document.activeElement === firstFocusableElement) {
  //           lastFocusableElement.focus();
  //           event.preventDefault();
  //         }
  //       } else {
  //         if (document.activeElement === lastFocusableElement) {
  //           firstFocusableElement.focus();
  //           event.preventDefault();
  //         }
  //       }
  //     }
  //   }
  // }

  function toNextStepFocus() {
    focusOnElement('.informativeSteps h3');
  }

  if (user?.extra?.task === false) return null;

  // Mesmos manipuladores do botão de fechar de hoje: antes do envio e depois dele ("Mandou bem!").
  const fecharDepoisDoEnvio = () => {
    sendPartialLog({ type: 'close', location: 'submitted' });
    setIsOpen(false);
    document.dispatchEvent(new CustomEvent('reload:partialTask'));
  };

  const fecharAntesDoEnvio = () => {
    if (searchResults.length > 0) {
      sendPartialLog({ type: 'close', location: 'header', reset: true });
      reset();
    } else {
      sendPartialLog({ type: 'close', location: 'header', reset: false });
      setIsOpen(false);
      document.dispatchEvent(new CustomEvent('reload:partialTask'));
    }
  };

  const desfecho: DesfechoDoCadastro | undefined = !submitted
    ? undefined
    : submitError
      ? {
          tipo: 'erro',
          titulo: !!submitError.match(/limite de participações/gi)
            ? 'Ocorreu um erro ao enviar essa Realização Destaque!'
            : 'Não deu pra enviar essa Realização Destaque!',
          mensagem: submitError,
        }
      : {
          tipo: 'sucesso',
          mensagem: `Sua Realização Destaque foi ${params.id && params.id !== 'cadastrar' ? 'atualizada' : 'cadastrada'}. Agora você poderá acompanhar o processo de validação da entrega que será realizado pela liderança.`,
          onAcompanhar: fecharDepoisDoEnvio,
        };

  return (
    <ModalDeCadastro
      key={'modal-create-task'}
      isOpen={isOpen}
      titulo={`${params.id && params.id !== 'cadastrar' ? 'Editar' : 'Cadastrar'} Realização Destaque`}
      etapas={ETAPAS_NA_TELA}
      etapaAtual={posicaoNaTela(step)}
      tituloDaEtapa={TITULOS_DAS_ETAPAS[step]}
      lembrete="Você só pode cadastrar 1 realização neste mês (fase) e ela deve estar concluída."
      onFechar={submitted ? fecharDepoisDoEnvio : fecharAntesDoEnvio}
      onRequestClose={
        submitted
          ? () => {
              setIsOpen(false);
              document.dispatchEvent(new CustomEvent('reload:partialTask'));
            }
          : () => setIsOpen(false)
      }
      onAfterClose={handleCloseModal}
      classeDoFormulario="informativeSteps"
      desfecho={desfecho}
      rodape={
        !loading && (
          <>
            {hasPartialTask && params?.id === 'cadastrar' && (
              <Button variant="texto" size="sm" onClick={handleClearDraft} aria-label="Limpar rascunho salvo e começar do início">
                Limpar rascunho
              </Button>
            )}
            <div className="ml-auto flex gap-2.5">
              {etapaParaVoltar !== undefined && (
                <Button
                  variant="secundario"
                  aria-label={`Voltar para a etapa ${posicaoNaTela(etapaParaVoltar) + 1} de cadastramento de realização destaque`}
                  onClick={() => {
                    sendPartialLog({
                      type: 'back',
                      location: 'bottom',
                      data: 'setStep(' + etapaParaVoltar + ')',
                    });
                    setStep(etapaParaVoltar);
                  }}
                >
                  Voltar
                </Button>
              )}
              {etapaParaContinuar !== undefined && (
                <Button
                  variant="solido-destaque"
                  onClick={() => {
                    sendPartialLog({
                      type: 'next',
                      location: 'bottom',
                      setStep: etapaParaContinuar,
                    });

                    if (!buttonContinueIsDisabled()) {
                      savePartialTask({ step: etapaParaContinuar });
                      setStep(etapaParaContinuar);
                    }
                  }}
                  disabled={buttonContinueIsDisabled()}
                  aria-label={`Continuar para a etapa ${posicaoNaTela(etapaParaContinuar) + 1}.`}
                >
                  Continuar
                </Button>
              )}
              {step === ultimaEtapa() && (
                <Button
                  variant="solido-destaque"
                  disabled={finalizarDesabilitado()}
                  onClick={() => handleSubmit()}
                  aria-label={`${params.id && params.id !== 'cadastrar' ? 'Atualizar' : 'Finalizar'} cadastramento de realização destaque`}
                >
                  {params.id && params.id !== 'cadastrar' ? 'Atualizar' : 'Finalizar'}
                </Button>
              )}
            </div>
          </>
        )
      }
    >
      {loading && !submitted && (
        // O rodapé com o botão focado sai durante o envio: o foco vem para o aviso (WCAG 2.4.3).
        <p
          ref={focarAoMontar}
          role="status"
          tabIndex={-1}
          className="m-0 animate-pulse py-12 outline-none text-center font-display text-lg font-bold text-brand-blue-dark">
          Enviando Realização Destaque...
        </p>
      )}

      {!loading && !submitted && !cargaInicialConcluida && (
        <p role="status" className="m-0 animate-pulse py-12 text-center text-xs font-bold text-brand-blue-dark">
          Carregando informações da realização...
        </p>
      )}

      {!loading && !submitted && cargaInicialConcluida && (
        <div className="flex flex-col gap-4">
          {!!helpData && (
            <p className="m-0 text-xs text-content-soft">
              Possui dúvidas ao preencher esta etapa? Confira o tutorial em vídeo.{' '}
              <button
                type="button"
                className={`font-bold uppercase text-brand-blue hover:underline ${FOCO_VISIVEL}`}
                onClick={() => showModal('help-video')}
              >
                Assistir vídeo
              </button>
            </p>
          )}

          {step === 0 && (
            <>
              <div>
                {!!(!target?.length || !teamApproval().blockAdd) && (
                  <div className="mb-3">
                    <PainelDeSugestoes
                      estado={sugestoes}
                      naLista={new Set((target ?? []).map(pessoa => pessoa._id))}
                      avaliar={pessoa => avaliarInclusao(pessoa, contextoDaInclusao(target ?? []))}
                      onIncluir={incluirSugeridas}
                    />
                  </div>
                )}
                <p id="cadastro-busca-instrucao" className={ROTULO}>
                  Inclua a equipe que efetivamente atuou com você para a conclusão da entrega, especificando o percentual de
                  participação do funcionário em uma escala de 25 a 100.
                </p>
                {!!(!target?.length || !teamApproval().blockAdd) && (
                  <form onSubmit={handleSearch} role="search" className="relative">
                    <InputMask
                      placeholder="Digite o nome ou matrícula do funcionário"
                      alwaysShowMask={false}
                      mask={mask}
                      ref={(ref: any) => {
                        searchRef.current = ref;
                      }}
                      onChange={e => handleChange()}
                      inputMode="search"
                      aria-label="Buscar funcionário pelo nome ou matrícula"
                      aria-describedby="cadastro-busca-instrucao"
                      className={`${CAMPO} ${FOCO_VISIVEL} pr-11`}
                    />
                    <button
                      type="button"
                      aria-label="Buscar funcionário"
                      onClick={() => handleSearch()}
                      className={`absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-brand-blue ${FOCO_VISIVEL}`}
                    >
                      <TbSearch size={18} aria-hidden="true" />
                    </button>
                  </form>
                )}
              </div>

              {!!(!target?.length || !teamApproval().blockAdd) && <ColagemDeChaves onIncluir={incluirVariasChaves} />}

              {!searching && isAlreadySelectedInList && (
                <AvisoDeValidacao>Esse usuário já foi inserido na lista de participantes.</AvisoDeValidacao>
              )}

              {!!searchResults && searchResults.length > 0 && (
                <div className="flex flex-col gap-2">
                  <ul className="m-0 flex max-h-[30vh] list-none flex-col gap-2 overflow-y-auto p-0 lg:max-h-48">
                    {searchResults.map((item, index) => (
                      <li key={item._id}>
                        <button
                          type="button"
                          onClick={() => {
                            sendPartialLog({ type: 'addTarget', data: item._id });
                            addTarget(item);
                          }}
                          className={`flex w-full items-center gap-3 rounded-jj-md border border-surface-300 bg-brand-white px-3 py-2.5 text-left hover:border-brand-blue ${FOCO_VISIVEL}`}
                        >
                          <RostoDaPessoa pessoa={item} />
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <span className="text-[12.5px] font-bold text-content">
                              {item.extra?.nome_guerra} <span className="font-normal text-content-soft">({item._id})</span>
                            </span>
                            <span className="text-jj-label text-content-soft">{item.name}</span>
                            {!!item.extra?.diretoria || !!item.extra?.funcao ? (
                              <span className="text-jj-label text-content-soft">
                                {!!item.extra?.diretoria && <span className="text-brand-blue">{item.extra?.diretoria}</span>}
                                {!!item.extra?.diretoria && !!item.extra?.funcao && ' · '}
                                {item.extra?.funcao ? `${item.extra?.funcao}` : ''}
                              </span>
                            ) : null}
                          </span>
                          <FiPlus aria-hidden="true" size={19} className="shrink-0 text-brand-blue" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <p className="m-0 text-xs text-content-soft">
                    Não encontrou quem procura? Experimente buscar por nome e sobrenome ou matrícula.
                  </p>
                </div>
              )}

              {!searching && isEmptyName && (
                <p className="m-0 text-xs text-content-soft">
                  Não encontrou quem procura? Experimente buscar por nome e sobrenome ou matrícula.
                </p>
              )}

              {isEmpty && !searching && (
                <AvisoDeValidacao>
                  Verifique a matrícula que você está buscando, essa matrícula não consta na base de funcionário do Joga Junto.
                </AvisoDeValidacao>
              )}

              {searching && (
                <p role="status" className="m-0 animate-pulse text-xs font-bold text-brand-blue-dark">
                  Recuperando informações...
                </p>
              )}

              {target && target?.length > 0 && !searching && (
                <div className="rounded-jj-lg bg-surface-100 px-4 py-3.5">
                  <div className="mb-1 flex flex-wrap items-center justify-between gap-2.5">
                    {target?.length && (
                      <p className="m-0 text-xs font-bold text-content">Total de participantes: {target?.length}</p>
                    )}
                    <p className="m-0 text-jj-micro text-content">
                      Até {maxMembersWithoutApproval} participantes sem autorização
                    </p>
                  </div>
                  <p className="m-0 mb-3 text-jj-label leading-normal text-content">
                    Adapte os percentuais, conforme a natureza da realização, a complexidade da atuação e as expectativas
                    específicas.
                  </p>

                  <div className="mb-3 flex flex-col gap-2 empty:hidden">
                    {!!teamApproval().showPending && (
                      <Nota>
                        A realização está aguardando a aprovação da quantidade de participantes pela gestão. A apreciação ocorre
                        normalmente, mas a realização só será pontuada após essa aprovação.
                      </Nota>
                    )}
                    {!!teamApproval().isApproved && (
                      <Nota>
                        A quantidade de participantes foi aprovada pela gestão. Caso altere os participantes, a realização poderá
                        passar por uma nova aprovação.
                      </Nota>
                    )}
                    {(!!teamApproval().expired || !!teamApproval().rejected) && (
                      <Nota>
                        A quantidade de participantes ainda não foi aprovada pela gestão. A realização só será pontuada após a
                        aprovação da quantidade de participantes.
                      </Nota>
                    )}
                  </div>

                  <ListaDeParticipantes
                    participantes={target}
                    percentuais={teamDistribution ?? {}}
                    limites={{ min: minMessageTarget, max: maxMessageTarget }}
                    mostrarAtalhoDePendentes={!isValidDistribution || !isValidDistributionMessage.valid}
                    idDoUsuario={user?._id}
                    podeRemover={item => user?._id !== item._id && item?._id !== currentTask?.owner}
                    onRemover={(_item, index) => {
                      sendPartialLog({ type: 'removeTarget', data: index });
                      removeTarget(index);
                    }}
                    onEscolherPercentual={escolherPercentual}
                    onAplicarEmMassa={aplicarPercentualEmMassa}
                    dicas={Object.fromEntries(Array.from(comPercentualDaUltimaVez, chave => [chave, 'Percentual da última vez']))}
                    editorDe={(item, index) => (
                      // A key remonta o editor quando o percentual muda: o draft-js cria o editorState uma única vez,
                      // então trocar `initialValue` não bastaria para o texto padrão aparecer (ou sumir) no campo.
                      <TextEditor
                        key={`${item._id}-${teamDistribution[item._id]}`}
                        disableEmoji
                        appearance="light"
                        ariaLabel={`Descreva a atuação de ${nomeDoParticipante(item)}`}
                        placeholder="Qual foi a contribuição desta pessoa?"
                        initialValue={item.message}
                        maxLength={maxMessageTarget}
                        minLength={minMessageTarget}
                        onChange={(html, length) => handleChangeMessageTarget(html, length, index)}
                      ></TextEditor>
                    )}
                  />
                </div>
              )}

              {target && target?.length > 0 && !isValidDistribution && (
                <AvisoDeValidacao>
                  Para continuar, preencha o percentual estimado de atuação de cada membro da equipe.
                </AvisoDeValidacao>
              )}

              {target && target?.length > 0 && isValidDistribution && !isValidDistributionMessage.valid && (
                <>
                  {!!isValidDistributionMessage.hasFieldsEmpty && (
                    <AvisoDeValidacao>
                      Para continuar, preencha o texto descrevendo a atuação de cada membro da equipe.
                    </AvisoDeValidacao>
                  )}

                  {!isValidDistributionMessage.hasFieldsEmpty &&
                  typeof isValidDistributionMessage.enteredMinimum === `boolean` &&
                  !isValidDistributionMessage.enteredMinimum ? (
                    <AvisoDeValidacao>
                      Preencha a atuação de cada membro da equipe com no mínimo <strong>{minMessageTarget}</strong> caracteres.
                    </AvisoDeValidacao>
                  ) : !isValidDistributionMessage.hasFieldsEmpty && isValidDistributionMessage.exceededMaximum ? (
                    <AvisoDeValidacao>
                      Preencha a atuação de cada membro da equipe com no máximo <strong>{maxMessageTarget}</strong> caracteres.
                    </AvisoDeValidacao>
                  ) : null}
                </>
              )}

              {!!teamApproval().showAlert && (
                <Nota alerta>
                  <button
                    type="button"
                    aria-expanded={!!openTargetAlert}
                    onClick={() => setOpenTargetAlert(prev => !prev)}
                    className={`flex w-full items-center justify-between gap-3 text-left text-xs font-bold text-brand-blue-dark ${FOCO_VISIVEL}`}
                  >
                    Atenção às regras de aprovação para a quantidade de participantes
                    {openTargetAlert ? (
                      <MdOutlineExpandLess aria-hidden="true" size={24} className="shrink-0" />
                    ) : (
                      <MdOutlineExpandMore aria-hidden="true" size={24} className="shrink-0" />
                    )}
                  </button>
                  {!!openTargetAlert && <>{textRecomendation()}</>}
                </Nota>
              )}
            </>
          )}

          {step === 1 && (
            <>
              <div ref={ref => (titleRef.current = ref)}>
                <p className={ROTULO} aria-hidden="true">
                  Nome da realização
                </p>
                <TextEditor
                  disableEmoji
                  appearance="light"
                  ariaLabel="Nome da realização"
                  placeholder="Dê um nome curto e claro para a sua entrega"
                  initialValue={title}
                  maxLength={maxTitleLength}
                  minLength={minTitleLength}
                  onChange={handleChangeTitle}
                  className="w-full text-[13px] text-content"
                />
              </div>

              <SelectField
                label="Indicador"
                value={indicator}
                placeholder="Selecione o indicador ligado a Realização Destaque"
                options={
                  indicatorsList
                    ? indicatorsList.map((item, index: number) => {
                        return {
                          label: item.nome,
                          value: item.cod,
                          bloco: item.bloco,
                          cod_bloco: item.cod_bloco,
                        };
                      })
                    : [
                        {
                          label: 'Sem indicador',
                          value: '',
                        },
                      ]
                }
                onChange={handleChangeIndicator}
              />

              {(needNec.length > 0 || selectedNec?.label) && (
                <SelectField
                  label="Número da NEC/Iniciativa"
                  hint='Caso não exista Necessidade de TI ou Iniciativa vinculada, escreva a sequência "123456789". Será obrigatório preencher o campo demandante.'
                  onInputChange={(e: any) => handleInputChange(e)}
                  value={selectedNec}
                  onBlur={() => setNecIsSearched(true)}
                  placeholder="Selecione a NEC ou a Iniciativa ligada a Realização Destaque"
                  options={
                    necsList
                      ? [
                          ...necsList.map((item: any, index: number) => {
                            return {
                              label: item.cd_alcc + ' - ' + item.nm_alcc,
                              value: item,
                            };
                          }),
                          {
                            label: '123456789 - SEM NEC/INICIATIVA VINCULADA',
                            value: { cd_alcc: '123456789', nm_alcc: 'SEM NEC/INICIATIVA VINCULADA' },
                          },
                        ]
                      : [
                          {
                            label: 'Carregando...',
                            value: '',
                          },
                        ]
                  }
                  onChange={(e: any) => [handleChangeNec(e)]}
                />
              )}

              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex-1">
                  <label htmlFor="cadastro-inicio" className={ROTULO}>
                    Início da entrega
                  </label>
                  <input
                    id="cadastro-inicio"
                    type="date"
                    defaultValue={periodStart}
                    min={PERIOD_START_MIN}
                    max={maxDate}
                    ref={ref => (periodStartRef.current = ref)}
                    onChange={handleChangePeriodStart}
                    className={`${CAMPO} ${FOCO_VISIVEL}`}
                  />
                </div>

                <div className="flex-1">
                  <label htmlFor="cadastro-conclusao" className={ROTULO}>
                    Conclusão da entrega
                  </label>
                  <input
                    id="cadastro-conclusao"
                    defaultValue={periodEnd}
                    type="date"
                    min={PERIOD_END_MIN}
                    max={maxDate}
                    ref={ref => (periodEndRef.current = ref)}
                    onChange={handleChangePeriodEnd}
                    className={`${CAMPO} ${FOCO_VISIVEL}`}
                  />
                </div>
              </div>

              {(needNec.length > 0 || selectedNec?.label) &&
                (selectedNec?.value && selectedNec?.value?.cd_alcc !== '123456789' ? (
                  <div>
                    <label htmlFor="demandante" className={ROTULO}>
                      Demandante
                    </label>
                    <input
                      id="demandante"
                      type="text"
                      value={demandanteValue}
                      onChange={handleDemandanteChange}
                      readOnly={selectedNec?.value && selectedNec?.value?.cd_alcc !== '123456789'}
                      className={`${CAMPO} ${FOCO_VISIVEL}`}
                    />
                  </div>
                ) : (
                  <SelectField
                    label="Demandante"
                    value={demandanteValue}
                    placeholder="Selecione o demandante ligado a Realização Destaque"
                    options={demandanteList}
                    isOptionSelected={isDemandanteOptionSelected}
                    disabled={!selectedNec?.value}
                    onChange={handleDemandanteChange}
                  />
                ))}

              <div>
                <label htmlFor="cadastro-responsavel" className={ROTULO}>
                  Responsável por apreciar a realização
                </label>

                <div className="flex items-center gap-2">
                  <form onSubmit={handleSearchManager} className="flex-1">
                    <input
                      id="cadastro-responsavel"
                      aria-describedby="cadastro-responsavel-orientacao"
                      value={
                        !!initialManager && !!initialManager.nome_guerra
                          ? `${initialManager.nome_guerra} (${initialManager.chave}) - ${initialManager.funcao}${
                              userInfo?.mad && initialPartner?.nome_guerra
                                ? ` e ${initialPartner.nome_guerra} (${initialPartner.chave})`
                                : ''
                            }`
                          : searchManager
                      }
                      disabled={!!initialManager}
                      onChange={e => setSearchManager(e.target.value)}
                      placeholder={!initialManager ? 'Digite o nome ou matrícula do gestor' : undefined}
                      className={`${CAMPO} ${FOCO_VISIVEL}`}
                    />
                  </form>

                  {!!initialManager ? (
                    <button
                      type="button"
                      onClick={() => {
                        sendPartialLog({
                          type: 'setInitialManager',
                          data: 'ND',
                        });
                        setInitialManager(null);
                        // Inerte fora do MAD (initialPartner já é undefined).
                        setInitialPartner(undefined);
                      }}
                      aria-label="Alterar responsável por apreciação."
                      className={BOTAO_DO_RESPONSAVEL}
                    >
                      <FiEdit2 aria-hidden="true" size={17} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleSearchManager()}
                      aria-label="Buscar responsável por apreciação."
                      className={BOTAO_DO_RESPONSAVEL}
                    >
                      <RiSearchLine aria-hidden="true" size={17} />
                    </button>
                  )}
                </div>

                {searching && (
                  <p role="status" className="m-0 mt-2 animate-pulse text-xs font-bold text-brand-blue-dark">
                    Recuperando informações...
                  </p>
                )}

                {!!searchResultsManager && searchResultsManager.length > 0 && (
                  <div className="mt-3">
                    <p className={ROTULO}>Resultado da pesquisa:</p>
                    <ul className="m-0 flex max-h-[30vh] list-none flex-col gap-2 overflow-y-auto p-0 lg:max-h-48">
                      {searchResultsManager.map(item => (
                        <li key={item._id}>
                          <button
                            type="button"
                            onClick={() => changeManager(item)}
                            className={`flex w-full items-center gap-3 rounded-jj-md border border-surface-300 bg-brand-white px-3 py-2.5 text-left hover:border-brand-blue ${FOCO_VISIVEL}`}
                          >
                            <Iniciais nome={item.nome_guerra || item.nome} />
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <span className="text-[12.5px] font-bold text-content">{item.nome_guerra}</span>
                              <span className="text-jj-label text-content-soft">
                                {item.nome} ({item.chave})
                              </span>
                              {!!item?.diretoria || !!item?.funcao ? (
                                <span className="text-jj-label text-content-soft">
                                  {!!item?.diretoria && <span className="text-brand-blue">{item?.diretoria}</span>}
                                  {!!item?.diretoria && !!item?.funcao && ' · '}
                                  {item?.funcao ? `${item?.funcao}` : ''}
                                </span>
                              ) : null}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                    <p className="m-0 mt-2 text-xs text-content-soft">
                      Não encontrou quem procura? Experimente buscar por nome e sobrenome ou matrícula.
                    </p>
                  </div>
                )}

                {/* Resultados como opções de dupla (fluxo MAD, MOD-07): cada gestor aparece
                    como opção solo e, quando tem dupla, também como "gestor e parceiro". */}
                {!!userInfo?.mad && searchResultsDupla.length > 0 && (
                  <div className="mt-3">
                    <p className={ROTULO}>Resultado da pesquisa:</p>
                    <ul className="m-0 flex max-h-[30vh] list-none flex-col gap-2 overflow-y-auto p-0 lg:max-h-48">
                      {searchResultsDupla.map((option, index) => {
                        const gestor = option.gestor;
                        const parceiro = option.parceiro;

                        return (
                          <li key={`${gestor?._id ?? ''}::${parceiro?._id ?? ''}-${index}`}>
                            <button
                              type="button"
                              onClick={() => changeManagerDupla(option)}
                              className={`flex w-full items-center gap-3 rounded-jj-md border border-surface-300 bg-brand-white px-3 py-2.5 text-left hover:border-brand-blue ${FOCO_VISIVEL}`}
                            >
                              <Iniciais nome={gestor?.nome_guerra} />
                              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                                <span className="text-[12.5px] font-bold text-content">
                                  {parceiro?.nome_guerra
                                    ? `${gestor?.nome_guerra} e ${parceiro.nome_guerra}`
                                    : `${gestor?.nome_guerra} (sem dupla)`}
                                </span>
                                <span className="text-jj-label text-content-soft">
                                  {gestor?.nome} ({gestor?.chave})
                                  {parceiro?.chave ? ` + ${parceiro.nome} (${parceiro.chave})` : ''}
                                </span>
                                {!!(gestor as any)?.diretoria || !!gestor?.funcao ? (
                                  <span className="text-jj-label text-content-soft">
                                    {!!(gestor as any)?.diretoria && (
                                      <span className="text-brand-blue">{(gestor as any)?.diretoria}</span>
                                    )}
                                    {!!(gestor as any)?.diretoria && !!gestor?.funcao && ' · '}
                                    {gestor?.funcao ? `${gestor.funcao}` : ''}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                    <p className="m-0 mt-2 text-xs text-content-soft">
                      Não encontrou quem procura? Experimente buscar por nome e sobrenome ou matrícula.
                    </p>
                  </div>
                )}
              </div>

              <div id="cadastro-responsavel-orientacao">
                <Nota>
                  <p className="m-0">
                    Essa pessoa é responsável por realizar a apreciação desta entrega? Caso não seja ela, você pode alterá-la
                    clicando no ícone ao lado do campo.
                  </p>
                  <p className="m-0 mt-2.5">
                    <b>Atenção:</b> não deve ser indicado novo líder nas situações em que o seu gestor tenha ausência temporária
                    (férias, abonos, licença paternidade, folgas, viagem a serviço, licença saúde até 15 (quinze) dias etc.).
                  </p>
                </Nota>
              </div>

              {necCheckFailed ? (
                <AvisoDeValidacao>
                  Não foi possível verificar o NEC/Iniciativa vinculada ao seu UOR. Recarregue a página e tente novamente.
                </AvisoDeValidacao>
              ) : null}

              {!!periodStart?.length && !periodStartIsValid ? (
                <AvisoDeValidacao>
                  A data de início da entrega deve ser a partir de 01/01/2025. Altere para prosseguir.
                </AvisoDeValidacao>
              ) : null}

              {!!periodEnd?.length && !periodEndIsValid ? (
                <AvisoDeValidacao>
                  A data da entrega deve estar entre 01/07/2024 e último dia deste mês. Altere para prosseguir.
                </AvisoDeValidacao>
              ) : null}

              {!!title && !indicator?.value ? (
                <AvisoDeValidacao>
                  {!!indicatorsList
                    ? 'Você precisa selecionar um indicador para continuar.'
                    : 'Você não possui indicador, portanto não pode cadastrar realização.'}
                </AvisoDeValidacao>
              ) : null}

              {needNec.length > 0 && !selectedNec?.value && necIsSearched ? (
                <AvisoDeValidacao>Você precisa selecionar um NEC/INICIATIVA para essa realização.</AvisoDeValidacao>
              ) : null}

              {missingDemandante ? (
                <AvisoDeValidacao>Você precisa selecionar um Demandante para essa realização.</AvisoDeValidacao>
              ) : null}

              {!titleLength ||
                titleLength?.textLength < minTitleLength ||
                (titleLength?.fullLength > maxTitleLength && (
                  <AvisoDeValidacao>
                    O Nome da realização inserido ultrapassou o limite máximo de {maxTitleLength} caracteres.
                  </AvisoDeValidacao>
                ))}
            </>
          )}

          {step === 2 && (
            <>
              <div ref={ref => (messageRef.current = ref)}>
                <p className={ROTULO} aria-hidden="true">
                  Detalhes da Realização Destaque
                </p>
                <div className="h-40 w-full">
                  <TextEditor
                    disableEmoji
                    appearance="light"
                    ariaLabel="Detalhes da Realização Destaque"
                    placeholder="Descreva o que foi entregue e como você chegou lá"
                    initialValue={message}
                    maxLength={maxMessageLength}
                    minLength={minMessageLength}
                    onChange={handleChangeMessage}
                  ></TextEditor>
                </div>
              </div>

              <div>
                <p className={ROTULO} aria-hidden="true">
                  Resultados da Realização Destaque
                </p>
                <div className="h-40 w-full">
                  <TextEditor
                    disableEmoji
                    appearance="light"
                    ariaLabel="Resultados da Realização Destaque"
                    placeholder="Aponte os resultados e benefícios alcançados"
                    initialValue={resultMessage}
                    maxLength={maxResultLength}
                    minLength={minResultLength}
                    onChange={handleChangeResult}
                  ></TextEditor>
                </div>
              </div>

              <p className="m-0 text-xs leading-normal text-brand-blue">
                Na descrição, não se esqueça de demonstrar clareza e objetividade dos resultados e benefícios alcançados por
                meio da sua realização destaque e fique bem na foto!
              </p>

              <Nota>
                <p className="m-0">
                  Atente-se quanto à classificação do sigilo das informações (IN 421), considerando que o conteúdo ficará
                  visível a todos os participantes.
                </p>
              </Nota>

              {necCheckFailed ? (
                <AvisoDeValidacao>
                  Não foi possível verificar o NEC/Iniciativa vinculada ao seu UOR. Recarregue a página e tente novamente.
                </AvisoDeValidacao>
              ) : null}
              {missingNec ? (
                <AvisoDeValidacao>Você precisa selecionar um NEC/INICIATIVA para essa realização.</AvisoDeValidacao>
              ) : null}
              {missingDemandante ? (
                <AvisoDeValidacao>Você precisa selecionar um Demandante para essa realização.</AvisoDeValidacao>
              ) : null}

              {!messageLength ||
                messageLength?.textLength < minMessageLength ||
                (messageLength?.fullLength > maxMessageLength && (
                  <AvisoDeValidacao>
                    O Detalhes da Realização Destaque inserido ultrapassou o limite máximo de {maxMessageLength} caracteres.
                  </AvisoDeValidacao>
                ))}
              {!resultLength ||
                resultLength?.textLength < minResultLength ||
                (resultLength?.fullLength > maxResultLength && (
                  <AvisoDeValidacao>
                    O Detalhes da Realização Destaque inserido ultrapassou o limite máximo de {maxResultLength} caracteres.
                  </AvisoDeValidacao>
                ))}
            </>
          )}
        </div>
      )}

      <NoticeModal
        isOpen={!!showModalAlert}
        title="Ops!"
        actionLabel="Entendi"
        onAction={() => setShowModalAlert(false)}
        onClose={() => setShowModalAlert(false)}
        closeLabel="Fechar aviso"
      >
        <p className="m-0">{showModalAlert}</p>
      </NoticeModal>
      {showModalTutorial && (
        <Modal
          onClose={() => {
            setshowModalTutorial(false);
          }}
          description={''}
        />
      )}
    </ModalDeCadastro>
  );
}
